import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { commercialConfigAPI, equipmentAPI, settingsAPI } from '../services/api';
import { withReportProfile, unitReportAddress } from '../utils/reportProfiles';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import { STANDARD_MATERIALS_OF_CONSTRUCTION } from '../utils/materialOptions';
import useApprovalRights from '../hooks/useApprovalRights';
import { toast } from 'react-toastify';
import { ActionsMenu } from '../components/ActionsMenu';
import { ESignModal, Modal, ModalFooter, ModalSubheader } from '../components/Modals';
import VersionHistoryModal from '../components/VersionHistoryModal';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import { FaPlus, FaEdit, FaTrash, FaEye, FaDownload, FaUpload, FaSearch, FaCopy, FaSave, FaTools, FaCheck, FaFileAlt, FaPrint, FaHourglassHalf, FaCogs, FaTimes } from 'react-icons/fa';
import { StatCard } from '../components/UnifiedUIComponents';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { openPrintPreview, getActivePrintUnitName } from '../utils/printUtils';
import { matchesPlantScope } from '../utils/helpers';
import TablePagination from '../components/TablePagination';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const EQUIPMENT_STATUS = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved'
};

const APPROVAL_DECISION_OPTIONS = [
  { value: 'approve', label: 'Approve' },
  { value: 'return', label: 'Return' },
  { value: 'reject', label: 'Reject' },
  { value: 'edit_required', label: 'Edit Required' }
];

const normalizeDisplayStatus = (status) => {
  if (!status) return EQUIPMENT_STATUS.DRAFT;
  if (status === 'Active') return EQUIPMENT_STATUS.APPROVED;
  return status;
};
const getDeleteApprovalStage = (equipment) => {
  const displayStatus = normalizeDisplayStatus(equipment?.status);
  if (displayStatus === EQUIPMENT_STATUS.DRAFT) return 'draft';
  if (!equipment?.deletion_requested) return 'request';
  if (!equipment?.deletion_product_approved) return 'product';
  return 'qa';
};

const getDeleteActionLabel = (stage) => {
  if (stage === 'draft') return 'deactivate draft equipment';
  if (stage === 'request') return 'request equipment deactivation approval';
  if (stage === 'product') return 'complete Product deactivation approval stage';
  return 'complete QA deactivation approval stage';
};

const getDeleteConfirmMessage = (stage, equipmentName) => {
  if (stage === 'draft') return `Deactivate draft equipment "${equipmentName}" now?`;
  if (stage === 'request') return `Request deactivation approval for "${equipmentName}"?`;
  if (stage === 'product') return `Complete Product deactivation approval stage for "${equipmentName}"?`;
  return `Complete QA deactivation approval stage for "${equipmentName}" and finalize deactivation?`;
};

const getEquipmentStatusInfo = (equipment) => {
  const displayStatus = normalizeDisplayStatus(equipment?.status);

  if (equipment?.deletion_requested && !equipment?.deletion_qa_approved) {
    return {
      text: 'Pending Deletion Approval',
      tooltip: 'This equipment has been marked for deletion and is awaiting final approval.',
      badgeClass: 'badge-danger'
    };
  }

  if (displayStatus === EQUIPMENT_STATUS.PENDING_APPROVAL) {
    return {
      text: displayStatus,
      tooltip: 'This equipment is awaiting approval.',
      badgeClass: 'badge-info'
    };
  }

  if (displayStatus === EQUIPMENT_STATUS.DRAFT) {
    return {
      text: displayStatus,
      tooltip: 'This equipment is in a draft state and has not been submitted for approval.',
      badgeClass: 'badge-warning'
    };
  }

  if (displayStatus === EQUIPMENT_STATUS.APPROVED) {
    return {
      text: displayStatus,
      tooltip: 'This equipment is approved.',
      badgeClass: 'badge-success'
    };
  }

  return { text: displayStatus, tooltip: `Status: ${displayStatus}`, badgeClass: 'badge-secondary' };
};

const parseFiles = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [value];
    }
  }
  return [];
};

const normalizeContactType = (value) => {
  if (String(value || '').toLowerCase() === 'indirect') return 'Indirect';
  return 'Direct';
};

const toFileUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string' && /^https?:\/\//i.test(file)) return file;
  return `${API_ORIGIN}/uploads/${file}`;
};

const formatSurfaceAreaUnitLabel = (unit) => {
  const normalized = String(unit || '').trim().toLowerCase();
  if (normalized === 'cm2') return 'cm²';
  if (normalized === 'm2') return 'm²';
  if (normalized === 'ft2') return 'ft²';
  return unit || '';
};

const toDataUrlIfPossible = (src) => new Promise((resolve) => {
  if (!src) {
    resolve(null);
    return;
  }

  const trimmed = String(src).trim();
  if (!trimmed) {
    resolve(null);
    return;
  }

  if (trimmed.startsWith('data:image/')) {
    resolve(trimmed);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = trimmed;
});


const FileUploader = ({ label, files, onAdd, onRemove, id }) => {
  const fileList = Array.isArray(files) ? files : (files ? [files] : []);
  const [pendingAttachmentName, setPendingAttachmentName] = useState('');
  const getFileDisplayName = (file) => {
    if (file?.name) return file.name;
    const raw = String(file || '');
    const chunks = raw.split(/[\\/]/);
    return chunks[chunks.length - 1] || raw || 'Attachment';
  };

  const handleFileClick = (file) => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else if (typeof file === 'string') {
      window.open(file, '_blank');
    }
  };

  const buildNamedFiles = (selectedFiles) => {
    const custom = String(pendingAttachmentName || '').trim();
    if (!custom) return selectedFiles;
    return selectedFiles.map((file, index) => {
      const originalName = String(file?.name || '');
      const extMatch = originalName.match(/(\.[^./\\]+)$/);
      const ext = extMatch ? extMatch[1] : '';
      const customHasExt = /\.[^./\\]+$/.test(custom);
      const baseName = customHasExt ? custom : `${custom}${ext}`;
      const finalName = selectedFiles.length > 1
        ? baseName.replace(/(\.[^./\\]+)?$/, (_, foundExt = '') => `_${index + 1}${foundExt}`)
        : baseName;
      return new File([file], finalName, { type: file.type, lastModified: file.lastModified });
    });
  };

  return (
    <div className="form-group" style={{ minWidth: '0' }}>
      <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)', marginBottom: '4px', display: 'block' }}>{label}</label>
      <div style={{ border: '1px dashed var(--border-color)', borderRadius: '6px', padding: '8px', background: 'var(--card-bg)', minHeight: '38px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: fileList.length > 0 ? '8px' : '0' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{fileList.length > 0 ? `${fileList.length} file(s)` : 'No files'}</span>
          <input
            type="text"
            className="form-control"
            placeholder="Attachment name (optional)"
            value={pendingAttachmentName}
            onChange={(e) => setPendingAttachmentName(e.target.value)}
            style={{ maxWidth: '210px', height: '28px', fontSize: 'var(--fs-sm)', padding: '4px 8px' }}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => document.getElementById(id).click()}
            style={{ padding: '2px 8px', fontSize: 'var(--fs-sm)', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--primary-light)', background: 'var(--card-bg)' }}
          >
            <FaPlus style={{ fontSize: 'var(--fs-xs)' }} /> Add
          </button>
        </div>
        <input
          type="file"
          multiple
          id={id}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              const selectedFiles = Array.from(e.target.files);
              onAdd(buildNamedFiles(selectedFiles));
              setPendingAttachmentName('');
              e.target.value = '';
            }
          }}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        />
        {fileList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {fileList.map((file, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                  <span style={{ fontSize: 'var(--fs-md)' }}>[File]</span>
                  <span 
                    style={{ 
                      fontSize: 'var(--fs-sm)', 
                      color: 'var(--primary-color, #2563eb)', 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      maxWidth: '100px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }} 
                    title={getFileDisplayName(file)}
                    onClick={() => handleFileClick(file)}
                  >
                    {getFileDisplayName(file)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  style={{ border: 'none', background: 'none', color: 'var(--danger-color)', cursor: 'pointer', padding: '2px', display: 'flex' }}
                  title="Remove"
                >
                  <FaTrash style={{ fontSize: 'var(--fs-xs)' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EquipmentMaster = () => {
  // Equipment State
  const [equipment, setEquipment] = useState([]);
  const [equipmentParts, setEquipmentParts] = useState([]);
  const [equipmentLoading, setEquipmentLoading] = useState(true);
  const [trains, setTrains] = useState([]);
  const [showAddToTrainModal, setShowAddToTrainModal] = useState(false);
  const [targetTrainId, setTargetTrainId] = useState('');
  const [targetTrainSearchTerm, setTargetTrainSearchTerm] = useState('');
  const [showTargetTrainDropdown, setShowTargetTrainDropdown] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showEquipmentViewModal, setShowEquipmentViewModal] = useState(false);
  const [equipmentEditingId, setEquipmentEditingId] = useState(null);
  const [qmsLinked, setQmsLinked] = useState(false);
  const [changeRefGate, setChangeRefGate] = useState(null); // {equipment} while the gate is open for an Approved row
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);
  const editingApprovedEquipmentRef = useRef(false);

  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  const [viewingEquipment, setViewingEquipment] = useState(null);
  const [historyModal, setHistoryModal] = useState(null); // equipment row
  const [viewEquipmentTrainLinks, setViewEquipmentTrainLinks] = useState([]);
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
  const [productionLineFilter, setProductionLineFilter] = useState(localStorage.getItem('activeLine') || 'All');
  const [activePlantFilter, setActivePlantFilter] = useState(localStorage.getItem('activePlant') || 'All');
  const [trainFilter, setTrainFilter] = useState('All');
  const [equipmentSortBy, setEquipmentSortBy] = useState('equipment_code');
  const [equipmentSortOrder, setEquipmentSortOrder] = useState('asc');
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [equipmentCurrentPage, setEquipmentCurrentPage] = useState(1);
  const [statusCard, setStatusCard] = useState('All'); // All | Approved | Pending | Draft
  const [selectedEquipmentRow, setSelectedEquipmentRow] = useState(null); // track row click for actions
  const [partsSearchTerms, setPartsSearchTerms] = useState({});
  const [activePartDropdownIndex, setActivePartDropdownIndex] = useState(null);
  const [commercialLines, setCommercialLines] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pendingEquipmentDelete, setPendingEquipmentDelete] = useState(null);
  const [equipmentDeletionImpact, setEquipmentDeletionImpact] = useState(null);
  const [pendingEquipmentBulkAction, setPendingEquipmentBulkAction] = useState(null);
  const [pendingEquipmentApproval, setPendingEquipmentApproval] = useState(null); // equipment awaiting signature for approval
  const [pendingEquipmentImport, setPendingEquipmentImport] = useState(null); // import file awaiting signature
  const [equipmentFormData, setEquipmentFormData] = useState({
    equipment_code: '',
    equipment_name: '',
    equipment_type: 'Processing',
    production_line: localStorage.getItem('activeLine') || '',
    unit_plant: localStorage.getItem('activePlant') || '',
    linked_trains: [],
    is_dedicated: false,
    is_shared: false,
    reference_document: '',
    equipment_notes: '',
    attachedFiles: [],
    parts: [{ part_code: '', part_name: '', contact_type: 'Direct', cleaning_method: 'Manual', is_dedicated: false, is_shared: true, material_of_construction: '', surface_area: '', surface_area_unit: 'cm2', part_attachments: [] }],
    status: EQUIPMENT_STATUS.DRAFT
  });
  const [trainSearchTerm, setTrainSearchTerm] = useState('');
  const [showTrainSearchResults, setShowTrainSearchResults] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  useEffect(() => { if (!showESignModal) setPendingEquipmentImport(null); }, [showESignModal]);
  const [eSignMode, setESignMode] = useState('submit');
  const [eSignData, setESignData] = useState({ password: '', comment: '', decision: 'approve' });
  const [viewLoading, setViewLoading] = useState(false);
  const [viewTrainDataWarning, setViewTrainDataWarning] = useState('');
  const [reportSettings, setReportSettings] = useState({
    report_client_name: 'Client Name',
    report_client_address: 'Client Address',
    report_document_name: 'Equipment Master Report',
    report_document_rev_no: '01',
    report_client_logo: '',
    report_primeclean_logo: '',
    report_footer_tagline: 'Data is generated and handled by PrimeClean.',
    report_format_no_equipment_master: ''
  });
  const didInitialLoad = useRef(false);
  const initialEquipmentRef = useRef(null);
  // Snapshot the form the moment the modal opens so unchanged edits can skip the e-signature.
  useEffect(() => {
    initialEquipmentRef.current = showEquipmentModal
      ? { json: JSON.stringify(equipmentFormData), status: equipmentFormData.status }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEquipmentModal]);
  const mobileImportInputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const currentUserRole = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}')?.role || '';
    } catch {
      return '';
    }
  })();
  const normalizedCurrentUserRole = String(currentUserRole).trim().toLowerCase();
  const isCurrentUserSuperAdmin = normalizedCurrentUserRole === 'super admin';
  // Who may approve equipment: Settings → Approval Flow (Equipment Master), not a hardcoded role list.
  const { canEarlier: canBulkProductApprove, canFinal: canBulkQaApprove } = useApprovalRights('equipment.approval');
  const canUseBulkActions = isCurrentUserSuperAdmin || canBulkProductApprove || canBulkQaApprove;
  const selectedEquipmentIdSet = new Set(selectedEquipment.map((id) => String(id)));
  const selectedEquipmentRecords = equipment.filter((eq) => selectedEquipmentIdSet.has(String(eq.equipment_id)));
  const bulkEquipmentApproveEligible = selectedEquipmentRecords.filter((eq) => normalizeDisplayStatus(eq.status) === EQUIPMENT_STATUS.PENDING_APPROVAL && !eq.deletion_requested);
  const bulkEquipmentDeleteProductEligible = selectedEquipmentRecords.filter((eq) => eq.deletion_requested && !eq.deletion_product_approved);
  const bulkEquipmentDeleteQaEligible = selectedEquipmentRecords.filter((eq) => eq.deletion_requested && !eq.deletion_qa_approved && (eq.deletion_product_approved || isCurrentUserSuperAdmin));

  const normalizeLoadErrorMessage = (error, fallback) => {
    const message = String(error?.response?.data?.message || error?.message || '').trim().toLowerCase();
    if (message === 'authentication error' || message === 'failed to validate session') {
      return `${fallback} (server validation issue)`;
    }
    return fallback;
  };

  const getTrainCodeLabel = (train) => {
    const code = String(train?.train_code || '').trim();
    if (code) return code;
    const trainId = String(train?.train_id || '').trim();
    return trainId ? `TRN-${trainId.padStart(3, '0')}` : 'TRN-UNK';
  };

  const getTrainDisplayLabel = (train) => {
    if (!train) return 'Unknown Train';
    return `${getTrainCodeLabel(train)} - ${train.train_name || 'Unnamed Train'}`;
  };

  const extractTrainRows = (payload) => {
    if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const refreshTrainOptions = async () => {
    try {
      const res = await equipmentAPI.getTrains();
      const rows = extractTrainRows(res);
      setTrains(rows);
      return rows;
    } catch (error) {
      console.warn(normalizeLoadErrorMessage(error, 'Failed to load equipment trains'));
      setTrains((prev) => (prev.length ? prev : []));
      return [];
    }
  };

  // Load Equipment
  const loadEquipmentData = async () => {
    try {
      setEquipmentLoading(true);
      
      const [equipmentRes, partsRes, commercialRes, trainsRes] = await Promise.allSettled([
        equipmentAPI.getAll(),
        equipmentAPI.getParts(),
        commercialConfigAPI.getFull(),
        equipmentAPI.getTrains()
      ]);

      const eqRows = equipmentRes.status === 'fulfilled' ? (equipmentRes.value?.data?.data?.rows || []) : [];
      const normalizedPartRows = partsRes.status === 'fulfilled' ? (partsRes.value?.data?.data || []) : [];
      const commercialPayload = commercialRes.status === 'fulfilled' ? commercialRes.value : null;
      const trainsPayload = trainsRes.status === 'fulfilled' ? trainsRes.value : { data: { data: [] } };

      setEquipment(eqRows);
      setEquipmentParts(normalizedPartRows);
      const configuredLines = commercialPayload?.data?.data?.lines || [];
      setCommercialLines(Array.isArray(configuredLines) ? configuredLines : []);
      setTrains(extractTrainRows(trainsPayload));
    } catch (error) {
      console.warn(normalizeLoadErrorMessage(error, 'Failed to load equipment data'));
      console.error(error);
    } finally {
      setEquipmentLoading(false);
    }
  };

  useEffect(() => {
    if (!didInitialLoad.current) {
      didInitialLoad.current = true;
      loadEquipmentData();
      loadReportSettings();
    }
  }, []);

  useEffect(() => {
    const sync = () => {
      setProductionLineFilter(localStorage.getItem('activeLine') || 'All');
      setActivePlantFilter(localStorage.getItem('activePlant') || 'All');
      setTrainFilter('All');
    };
    window.addEventListener('site-context-changed', sync);
    return () => window.removeEventListener('site-context-changed', sync);
  }, []);

  useEffect(() => {
    if (location.state?.openViewEquipmentId && equipment.length > 0 && !showEquipmentViewModal && !showEquipmentModal) {
      const requestedEquipmentId = String(location.state.openViewEquipmentId);
      const selectedEquipment = equipment.find((eq) => String(eq.equipment_id) === requestedEquipmentId);
      if (selectedEquipment) {
        (async () => {
          await handleEquipmentView(selectedEquipment);
          navigate(location.pathname, { replace: true, state: {} });
        })();
      }
      return;
    }

    if (location.state?.openEditEquipmentId && equipment.length > 0 && !showEquipmentModal) {
      const requestedEquipmentId = String(location.state.openEditEquipmentId);
      const selectedEquipment = equipment.find((eq) => String(eq.equipment_id) === requestedEquipmentId);
      if (selectedEquipment) {
        handleEquipmentEdit(selectedEquipment);
        navigate(location.pathname, { replace: true, state: {} });
      }
      return;
    }

    // Carries the status segment a dashboard chart was drilled into (e.g. clicking
    // the "Pending Approval" bar in Equipment Status) through to this list's own
    // status filter cards, instead of landing on an unfiltered list.
    if (location.state?.filterStatus) {
      const card = { Draft: 'Draft', 'Pending Approval': 'Pending', Approved: 'Approved' }[location.state.filterStatus] || 'All';
      setStatusCard(card);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (location.state?.openAddEquipmentModal && !showEquipmentModal) {
      resetEquipmentForm();
      setShowEquipmentModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, showEquipmentModal, showEquipmentViewModal, equipment]);

  // Equipment Form Handlers
  const resetEquipmentForm = () => {
    setEquipmentFormData({
      equipment_code: '',
      equipment_name: '',
      equipment_type: 'Processing',
      production_line: localStorage.getItem('activeLine') || '',
      linked_trains: [], // Array of { train_id, product_type }
      is_dedicated: false,
      is_shared: false,
      reference_document: '',
      equipment_notes: '',
      attachedFiles: [],
      parts: [{ part_code: '', part_name: '', contact_type: 'Direct', cleaning_method: 'Manual', is_dedicated: false, is_shared: true, material_of_construction: '', surface_area: '', surface_area_unit: 'cm2', part_attachments: [] }],
      status: EQUIPMENT_STATUS.DRAFT
    });
    setEquipmentEditingId(null);
    setESignData({ password: '', comment: '', decision: 'approve' });
    setPartsSearchTerms({});
    setActivePartDropdownIndex(null);
    editingApprovedEquipmentRef.current = false;
    setPendingChangeRef(null);
  };

  const handleEquipmentAdd = () => {
    resetEquipmentForm();
    setShowEquipmentModal(true);
  };

  const handleEquipmentEdit = (eq) => {
    if (eq.status === EQUIPMENT_STATUS.APPROVED) {
      setPendingChangeRef(null);
      setChangeRefGate({ equipment: eq });
      return;
    }
    openEquipmentEditForm(eq);
  };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      editingApprovedEquipmentRef.current = true;
      openEquipmentEditForm(changeRefGate.equipment);
      setChangeRefGate(null);
    } finally {
      setChangeRefBusy(false);
    }
  };

  const openEquipmentEditForm = (eq) => {
    const linkedParts = equipmentParts.filter(p => p.equipment_id === eq.equipment_id);
    const trainsForEq = (Array.isArray(eq.linked_trains) ? eq.linked_trains : 
                        (Array.isArray(eq.train_links) ? eq.train_links : []))
                        .map(t => ({ 
                          train_id: t.train_id, 
                          train_code: t.train_code, 
                          train_name: t.train_name,
                          product_type: t.product_type || '' 
                        }));
    setEquipmentFormData({
      equipment_code: eq.equipment_code || '',
      equipment_name: eq.equipment_name || '',
      equipment_type: eq.equipment_type || 'Processing',
      production_line: eq.production_line || eq.production_line_name || eq.line_name || eq.line_code || '',
      linked_trains: trainsForEq,
      is_dedicated: eq.is_dedicated || false,
      is_shared: eq.is_shared || false,
      reference_document: eq.reference_document || '',
      equipment_notes: eq.equipment_notes || '',
      attachedFiles: parseFiles(eq.attached_files || eq.attachedFiles || []),
      parts: linkedParts.length > 0 ? linkedParts : [{ part_code: '', part_name: '', contact_type: 'Direct', cleaning_method: 'Manual', is_dedicated: false, is_shared: true, material_of_construction: '', surface_area: '', surface_area_unit: 'cm2', part_attachments: [] }],
      status: eq.status || EQUIPMENT_STATUS.DRAFT
    });
    setEquipmentFormData((prev) => ({
      ...prev,
      parts: (prev.parts || []).map((part) => ({
        ...part,
        cleaning_method: part.cleaning_method || 'Manual',
        is_dedicated: part.is_dedicated === true || Number(part.is_dedicated) === 1,
        is_shared: part.is_shared === true || Number(part.is_shared) === 1 || (!(part.is_dedicated === true || Number(part.is_dedicated) === 1) && (part.is_shared == null)),
        part_attachments: parseFiles(part.part_attachments)
      }))
    }));
    setPartsSearchTerms({});
    setActivePartDropdownIndex(null);
    setEquipmentEditingId(eq.equipment_id);
    setShowEquipmentModal(true);
  };

  const loadReportSettings = async () => {
    try {
      const response = await settingsAPI.getAll();
      const settings = response?.data?.data || {};
      setReportSettings((prev) => ({
        ...prev,
        report_client_name: settings.report_client_name || prev.report_client_name,
        report_client_address: settings.report_client_address || prev.report_client_address,
        report_document_name: settings.report_document_name || prev.report_document_name,
        report_document_rev_no: settings.report_document_rev_no || prev.report_document_rev_no,
        report_client_logo: settings.report_client_logo || prev.report_client_logo,
        report_primeclean_logo: settings.report_primeclean_logo || prev.report_primeclean_logo,
        report_footer_tagline: settings.report_footer_tagline || prev.report_footer_tagline,
        report_format_no_equipment_master: settings.report_format_no_equipment_master || prev.report_format_no_equipment_master
      }));
    } catch (error) {
      // Keep defaults
    }
  };

  const handleEquipmentDuplicate = (eq) => {
    // similar to product duplicate: open modal populated with copied info
    const existingCodes = new Set((equipment || []).map(item => String(item.equipment_code || '').trim().toLowerCase()));
    const baseCode = String(eq.equipment_code || 'EQ').trim() || 'EQ';
    const withMaxLen = (v) => String(v || '').slice(0, 100);
    let candidateCode = withMaxLen(`COPY-${baseCode}`);
    let copyIndex = 2;
    while (existingCodes.has(candidateCode.toLowerCase())) {
      candidateCode = withMaxLen(`COPY${copyIndex}-${baseCode}`);
      copyIndex += 1;
    }

    setEquipmentEditingId(null);
    setPartsSearchTerms({});
    setActivePartDropdownIndex(null);

    const attachedParts = equipmentParts
      .filter(p => p.equipment_id === eq.equipment_id)
      .map(p => ({ ...p, equipment_part_id: null }));

    setEquipmentFormData({
      ...eq,
      equipment_code: candidateCode,
      equipment_name: `${eq.equipment_name} (Copy)`,
      parts: attachedParts,
      status: EQUIPMENT_STATUS.DRAFT
    });

    setShowEquipmentModal(true);
  };

  const handleEquipmentView = async (eq) => {
    setViewingEquipment(eq);
    setShowEquipmentViewModal(true);
    setViewLoading(true);
    setViewEquipmentTrainLinks([]);
    setViewTrainDataWarning('');
    try {
      try {
        const directLinksRes = await equipmentAPI.getEquipmentTrainLinks(eq.equipment_id);
        const directLinks = extractTrainRows(directLinksRes)
          .filter((train) => train?.train_id)
          .map((train) => ({
            train_id: train.train_id,
            train_code: train.train_code,
            train_name: train.train_name
          }));
        if (directLinks.length > 0) {
          setViewEquipmentTrainLinks(directLinks);
          return;
        }
      } catch (error) {
        // Fallback to per-train scan if direct endpoint is unavailable.
      }

      const trainLinks = [];
      let hasUnresolvedTrainMembers = false;
      const equipmentIds = new Set((equipment || []).map((item) => String(item.equipment_id || '').trim()).filter(Boolean));
      await Promise.all(
        (trains || []).map(async (train) => {
          let members = [];
          try {
            const res = await equipmentAPI.getTrainMembers(train.train_id);
            members = Array.isArray(res?.data?.data)
              ? res.data.data
              : Array.isArray(res?.data)
                ? res.data
                : [];
          } catch (error) {
            // Ignore single-train read failure in view modal link check.
          }



          const missingMasterRows = members.some((member) => {
            const id = String(member.equipment_id || member.equipmentId || '').trim();
            return id && !equipmentIds.has(id);
          });
          if (missingMasterRows) {
            hasUnresolvedTrainMembers = true;
          }

          const targetEquipmentId = String(eq.equipment_id || '').trim();
          const targetEquipmentCode = String(eq.equipment_code || '').trim().toLowerCase();
          const targetEquipmentName = String(eq.equipment_name || '').trim().toLowerCase();
          const exists = members.some((member) => {
            const memberEquipmentId = String(member.equipment_id || '').trim();
            const memberEquipmentCode = String(member.equipment_code || '').trim().toLowerCase();
            const memberEquipmentName = String(member.equipment_name || '').trim().toLowerCase();
            const mergedMemberText = [
              member.equipment,
              member.equipment_display,
              member.equipment_label,
              member.equipment_text,
              member.equipment_id,
              member.equipment_code,
              member.equipment_name
            ]
              .filter(Boolean)
              .map((value) => String(value).trim().toLowerCase())
              .join(' ');
            return (
              (targetEquipmentId && memberEquipmentId === targetEquipmentId) ||
              (targetEquipmentCode && memberEquipmentCode === targetEquipmentCode) ||
              (targetEquipmentName && memberEquipmentName === targetEquipmentName) ||
              (targetEquipmentCode && mergedMemberText.includes(targetEquipmentCode)) ||
              (targetEquipmentName && targetEquipmentName.length >= 4 && mergedMemberText.includes(targetEquipmentName))
            );
          });
          if (exists) {
            trainLinks.push({
              train_id: train.train_id,
              train_code: train.train_code,
              train_name: train.train_name
            });
          }
        })
      );
      trainLinks.sort((a, b) => String(a.train_code || '').localeCompare(String(b.train_code || '')));
      setViewEquipmentTrainLinks(trainLinks);
      if (hasUnresolvedTrainMembers) {
        setViewTrainDataWarning('Equipment Train has unresolved rows (Unknown Equipment). Update from Equipment Master in Equipment Trains modal.');
      }
    } finally {
      setViewLoading(false);
    }
  };

  const handleAddToExistingTrain = () => {
    if (!targetTrainId) return;
    navigate('/equipment-trains', { state: { trainId: targetTrainId, selectedEquipmentIds: selectedEquipment } });
    setShowAddToTrainModal(false);
  };

  const handleEquipmentSave = async () => {
    if (!equipmentFormData.equipment_code.trim() || !equipmentFormData.equipment_name.trim()) {
      toast.error('Equipment Code and Name are required');
      return;
    }
    const partsToSave = equipmentFormData.parts.filter((part) => part.part_code && part.part_name);
    const invalidPartDedication = partsToSave.find((part) => Boolean(part.is_dedicated) === Boolean(part.is_shared));
    if (invalidPartDedication) {
      toast.error('For each part, select exactly one: Dedicated or Shared');
      return;
    }

    try {
      const hasDedicatedPart = partsToSave.some((part) => Boolean(part.is_dedicated));
      const hasSharedPart = partsToSave.some((part) => Boolean(part.is_shared));
      
      const linkedTrains = (equipmentFormData.linked_trains || []);

      const payload = {
        equipment_code: equipmentFormData.equipment_code,
        equipment_name: equipmentFormData.equipment_name,
        equipment_type: equipmentFormData.equipment_type || 'Processing',
        production_line: equipmentFormData.production_line || null,
        unit_plant: equipmentFormData.unit_plant || null,
        linked_trains: linkedTrains.map(t => ({ train_id: t.train_id, product_type: t.product_type })),
        is_dedicated: hasDedicatedPart ? 1 : 0,
        is_shared: hasSharedPart ? 1 : 0,
        reference_document: equipmentFormData.reference_document,
        equipment_notes: equipmentFormData.equipment_notes,
        attached_files: equipmentFormData.attachedFiles,
        status: equipmentFormData.status,
        esign_password: eSignData.password,
        esign_comment: eSignData.comment
      };
      if (equipmentEditingId && editingApprovedEquipmentRef.current && pendingChangeRef) {
        payload.change_reference_type = pendingChangeRef.reference_type;
        payload.change_reference_number = pendingChangeRef.reference_number;
        payload.change_reference_files = pendingChangeRef.reference_files || [];
      }

      let eqId = equipmentEditingId;
      if (equipmentEditingId) {
        await equipmentAPI.update(equipmentEditingId, payload);
        toast.success('Equipment updated successfully');
      } else {
        const res = await equipmentAPI.create(payload);
        eqId = res?.data?.equipment_id || res?.data?.data?.equipment_id;
        toast.success('Equipment created successfully');
      }

      // Save parts
      if (eqId) {
        for (const part of partsToSave) {
          const normalizedPartAttachments = parseFiles(part.part_attachments);
          if (part.equipment_part_id) {
            // Update existing part
            await equipmentAPI.updatePart(part.equipment_part_id, {
              ...part,
              part_attachments: normalizedPartAttachments,
              equipment_id: eqId
            });
          } else {
            // Create new part
            await equipmentAPI.createPart({
              ...part,
              part_attachments: normalizedPartAttachments,
              equipment_id: eqId
            });
          }
        }
      }

      setShowEquipmentModal(false);
      resetEquipmentForm();
      loadEquipmentData();
    } catch (error) {
      toast.error(error.message || 'Failed to save equipment');
    }
  };

  const handleEquipmentPartChange = (index, field, value) => {
    setEquipmentFormData(prevState => {
      const updatedParts = [...prevState.parts];
      updatedParts[index] = { ...updatedParts[index], [field]: value };
      return { ...prevState, parts: updatedParts };
    });
  };

  const addEquipmentPartToForm = () => {
    const newPart = { part_code: '', part_name: '', contact_type: 'Direct', cleaning_method: 'Manual', is_dedicated: false, is_shared: true, material_of_construction: '', surface_area: '', surface_area_unit: 'cm2', part_attachments: [] };
    setEquipmentFormData({ ...equipmentFormData, parts: [...equipmentFormData.parts, newPart] });
  };

  const removeEquipmentPartFromForm = (index) => {
    setEquipmentFormData({ ...equipmentFormData, parts: equipmentFormData.parts.filter((_, i) => i !== index) });
  };

  const applyPartSuggestion = (index, selectedPart) => {
    const updatedParts = [...equipmentFormData.parts];
    updatedParts[index] = {
      ...updatedParts[index],
      part_code: selectedPart.part_code || '',
      part_name: selectedPart.part_name || '',
      contact_type: selectedPart.contact_type || 'Direct',
      cleaning_method: selectedPart.cleaning_method || 'Manual',
      is_dedicated: selectedPart.is_dedicated === true || Number(selectedPart.is_dedicated) === 1,
      is_shared: selectedPart.is_shared === true || Number(selectedPart.is_shared) === 1 || (selectedPart.is_dedicated == null && selectedPart.is_shared == null),
      material_of_construction: selectedPart.material_of_construction || '',
      surface_area: selectedPart.surface_area || '',
      surface_area_unit: selectedPart.surface_area_unit || 'cm2',
      part_attachments: parseFiles(selectedPart.part_attachments)
    };
    setEquipmentFormData({ ...equipmentFormData, parts: updatedParts });
    setPartsSearchTerms({ ...partsSearchTerms, [index]: '' });
    setActivePartDropdownIndex(null);
  };

  const CANONICAL_LINES = ['Tablet Line', 'Capsule Line', 'Dry Syrup Line'];

  const normalizeEquipmentLineTags = (eq) => {
    const rawOriginal = String(
      eq?.production_line ||
      eq?.production_line_name ||
      eq?.line_name ||
      eq?.line_code ||
      ''
    ).trim();
    const raw = rawOriginal.toLowerCase();
    if (!raw) return [];
    if (raw === 'all') return [...CANONICAL_LINES];

    const tags = new Set();
    // Always include the exact stored production_line for direct section matching
    if (rawOriginal) tags.add(rawOriginal);
    // Legacy keyword-based canonical mapping for backward compat
    if (raw.includes('tablet')) tags.add('Tablet Line');
    if (raw.includes('capsule')) tags.add('Capsule Line');
    if (raw.includes('dry syrup') || raw.includes('drysyrup') || raw.includes('syrup')) tags.add('Dry Syrup Line');
    return Array.from(tags);
  };

  const getEquipmentProductionLine = (eq) => {
    const tags = normalizeEquipmentLineTags(eq);
    if (!tags.length) return 'Unassigned';
    if (tags.length === 1) return tags[0];
    return tags.join(' / ');
  };

  const productionLineFormOptions = useMemo(() => {
    const dynamicLines = commercialLines.map(l => l.line_name).filter(Boolean);
    return Array.from(new Set([...CANONICAL_LINES, ...dynamicLines])).sort();
  }, [commercialLines]);

  // Filter dropdown lines to only those present in equipment for the active plant
  const productionLineOptions = useMemo(() => {
    const lines = new Set();
    equipment.forEach(eq => {
      const matchesPlant = matchesPlantScope(eq.unit_plant || eq.scope_plant_code, activePlantFilter);
      if (matchesPlant) {
        normalizeEquipmentLineTags(eq).forEach(line => lines.add(line));
      }
    });
    const filtered = productionLineFormOptions.filter(l => lines.has(l));
    return ['All', ...filtered];
  }, [equipment, activePlantFilter, productionLineFormOptions]);

  // Suggestions mix the fixed canonical list (so a brand-new install still gets
  // consistent spellings before any parts exist) with whatever's already been typed —
  // this stays a free-text input (not a closed list) so an unusual, legitimate material
  // already on file isn't blocked, but new entries are nudged toward one spelling.
  const materialOptions = Array.from(new Set([
    ...STANDARD_MATERIALS_OF_CONSTRUCTION,
    ...equipmentParts.map((part) => (part.material_of_construction || '').trim()).filter(Boolean),
  ])).sort();

  const equipmentAreaTotals = useMemo(() => {
    const byEquipmentId = equipmentParts.reduce((acc, part) => {
      const key = part.equipment_id;
      if (!key) return acc;
      const area = Number.parseFloat(part.surface_area) || 0;
      const type = String(part.contact_type || '').toLowerCase();
      if (!acc[key]) {
        acc[key] = { direct: 0, indirect: 0, total: 0 };
      }
      if (type === 'indirect') {
        acc[key].indirect += area;
      } else {
        acc[key].direct += area;
      }
      acc[key].total += area;
      return acc;
    }, {});

    const overall = Object.values(byEquipmentId).reduce((acc, row) => ({
      direct: acc.direct + row.direct,
      indirect: acc.indirect + row.indirect,
      total: acc.total + row.total
    }), { direct: 0, indirect: 0, total: 0 });

    return { byEquipmentId, overall };
  }, [equipmentParts]);

  const handleEquipmentDelete = async (eq) => {
    if (!eq?.equipment_id) return;
    const stage = getDeleteApprovalStage(eq);
    setPendingEquipmentDelete({ equipment: eq, stage });
    setPendingEquipmentApproval(null);
    setESignMode(stage === 'draft' ? 'draft' : 'submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setEquipmentDeletionImpact(null);
    // Only worth checking at the first 'request' stage — by the Product/QA approval stages
    // the requester has already seen it, and re-fetching would just add latency.
    if (stage === 'request' || stage === 'draft') {
      try {
        const res = await equipmentAPI.getDeletionImpact(eq.equipment_id);
        setEquipmentDeletionImpact(res?.data?.data || null);
      } catch { /* non-blocking preview — ignore failures */ }
    }
    setShowESignModal(true);
  };

  const handleEquipmentStageApproval = (eq, decision = 'approve') => {
    // queue equipment for approval signing
    setPendingEquipmentApproval(eq);
    setPendingEquipmentDelete(null);
    setPendingEquipmentBulkAction(null);
    setESignMode('approval');
    setESignData({ password: '', comment: '', decision });
    setShowESignModal(true);
  };

  const handleBulkEquipmentApproval = (stage, approvalType) => {
    const eligibleEquipment = approvalType === 'delete'
      ? (stage === 'product' ? bulkEquipmentDeleteProductEligible : bulkEquipmentDeleteQaEligible)
      : bulkEquipmentApproveEligible;

    if (eligibleEquipment.length === 0) {
      toast.info('No selected equipment is eligible for this approval stage.');
      return;
    }

    setPendingEquipmentApproval(null);
    setPendingEquipmentDelete(null);
    setPendingEquipmentBulkAction({
      actionType: approvalType === 'delete' ? 'bulk_delete_approval' : 'bulk_approve',
      stage,
      approvalType,
      equipmentIds: eligibleEquipment.map((eq) => eq.equipment_id)
    });
    setESignMode('approval');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedEquipment.length === 0) return;
    if (!isCurrentUserSuperAdmin) {
      toast.error('Only Super Admin can bulk deactivate equipment');
      return;
    }
    setPendingEquipmentApproval(null);
    setPendingEquipmentDelete(null);
    setPendingEquipmentBulkAction({
      actionType: 'bulk_delete',
      equipmentIds: [...selectedEquipment]
    });
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  }; 

  // Editing an existing record with nothing changed: warn instead of asking for a signature.
  // Submitting a Draft for approval is still a real action, so that path is exempt.
  const isUnchangedEdit = (mode) => {
    if (!equipmentEditingId || !initialEquipmentRef.current) return false;
    const { json, status } = initialEquipmentRef.current;
    if (mode === 'submit' && status === EQUIPMENT_STATUS.DRAFT) return false;
    return json === JSON.stringify(equipmentFormData);
  };

  const openESignModalForDraftSave = () => {
    if (isUnchangedEdit('draft')) return toast.info('No changes were made — nothing to save, so no signature is required.');
    setESignMode('draft');
    setShowESignModal(true);
  };

  const openESignModalForActiveSave = () => {
    if (isUnchangedEdit('submit')) return toast.info('No changes were made — nothing to save, so no signature is required.');
    // set status to pending approval before saving
    setEquipmentFormData((prev) => ({ ...prev, status: EQUIPMENT_STATUS.PENDING_APPROVAL }));
    setESignMode('submit');
    setShowESignModal(true);
  };

  const handleConfirmESignAndSave = async (decisionOverride) => {
    if (!eSignData.password || !eSignData.comment) {
      toast.error('Password and comment are required');
      return;
    }
    if (pendingEquipmentImport) {
      const importFile = pendingEquipmentImport;
      setPendingEquipmentImport(null);
      setShowESignModal(false);
      await runEquipmentImport(importFile);
      setESignData({ password: '', comment: '', decision: 'approve' });
      return;
    }
    // handle pending approval first
    if (pendingEquipmentApproval) {
      setEquipmentLoading(true);
      try {
        const response = await equipmentAPI.approve(pendingEquipmentApproval.equipment_id, {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment,
          decision: decisionOverride || eSignData.decision
        });
        toast.success(response?.data?.message || 'Equipment approval completed');
        loadEquipmentData();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Equipment approval failed');
      } finally {
        setEquipmentLoading(false);
      }
      setPendingEquipmentApproval(null);
      setShowESignModal(false);
      setESignData({ password: '', comment: '', decision: 'approve' });
      return;
    }
    if (pendingEquipmentBulkAction) {
      setEquipmentLoading(true);
      try {
        if (pendingEquipmentBulkAction.actionType === 'bulk_delete') {
          const response = await equipmentAPI.bulkDelete(pendingEquipmentBulkAction.equipmentIds, {
            esign_password: eSignData.password,
            esign_comment: eSignData.comment
          });
          toast.success(response?.data?.message || 'Bulk deactivation processed successfully');
        } else {
          const response = await equipmentAPI.bulkApprove(
            pendingEquipmentBulkAction.equipmentIds,
            pendingEquipmentBulkAction.approvalType === 'delete' ? pendingEquipmentBulkAction.stage : 'approval',
            pendingEquipmentBulkAction.approvalType || 'standard',
            {
              esign_password: eSignData.password,
              esign_comment: eSignData.comment,
              ...(pendingEquipmentBulkAction.approvalType !== 'delete' ? { decision: decisionOverride || eSignData.decision } : {})
            }
          );
          const results = response?.data?.results || {};
          if (results.approved?.length) toast.success(`${results.approved.length} equipment record(s) approved`);
          if (results.alreadyDone?.length) toast.info(`${results.alreadyDone.length} equipment record(s) already approved`);
          if (results.skipped?.length) toast.info(`${results.skipped.length} equipment record(s) were not eligible`);
          if (results.missing?.length) toast.error(`${results.missing.length} equipment record(s) were not found`);
          if (!results.approved?.length && response?.data?.message) toast.info(response.data.message);
        }
        setSelectedEquipment([]);
        loadEquipmentData();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to process bulk equipment action');
      } finally {
        setEquipmentLoading(false);
      }
      setPendingEquipmentBulkAction(null);
      setShowESignModal(false);
      setESignData({ password: '', comment: '', decision: 'approve' });
      return;
    }

    // handle pending delete next
    if (pendingEquipmentDelete) {
      const { equipment, stage } = pendingEquipmentDelete;
      setEquipmentLoading(true);
      try {
        const payload = {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment
        };
        if (stage !== 'draft') payload.approval_stage = stage;
        await equipmentAPI.delete(equipment.equipment_id, payload);
        const successMessage = stage === 'draft'
          ? 'Equipment deactivated successfully'
          : (stage === 'request'
            ? 'Deactivation request submitted. Pending Product approver stage.'
            : (stage === 'product'
              ? 'Product deactivation approval completed. Pending QA deactivation approval.'
              : 'QA deactivation approval completed. Equipment deactivated successfully.'));
        toast.success(successMessage);
        loadEquipmentData();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to deactivate equipment');
      } finally {
        setEquipmentLoading(false);
      }
      setPendingEquipmentDelete(null);
      setShowESignModal(false);
      setESignData({ password: '', comment: '', decision: 'approve' });
      return;
    }

    await handleEquipmentSave();
    setShowESignModal(false);
    setESignData({ password: '', comment: '', decision: 'approve' });
  };

  // Trains available under the current plant + production line selection
  const linkedTrainOptions = useMemo(() => {
    const seen = new Map();
    equipment
      .filter(eq => {
        const matchesPlant = matchesPlantScope(eq.unit_plant || eq.scope_plant_code, activePlantFilter);
        const matchesLine = productionLineFilter === 'All' || normalizeEquipmentLineTags(eq).includes(productionLineFilter);
        return matchesPlant && matchesLine;
      })
      .forEach(eq => {
        (eq.linked_trains || []).forEach(t => {
          const id = String(t.train_id);
          if (!seen.has(id)) seen.set(id, t);
        });
      });
    return Array.from(seen.values()).sort((a, b) =>
      getTrainCodeLabel(a).localeCompare(getTrainCodeLabel(b))
    );
  }, [equipment, activePlantFilter, productionLineFilter]);

  // Sorting & Filtering
  const sortedEquipment = [...equipment].sort((a, b) => {
    let aVal = a[equipmentSortBy];
    let bVal = b[equipmentSortBy];
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (equipmentSortOrder === 'asc') {
      return String(aVal).localeCompare(String(bVal));
    } else {
      return String(bVal).localeCompare(String(aVal));
    }
  }).filter(eq => {
    const term = equipmentSearchTerm.toLowerCase();
    const activePlant = localStorage.getItem('activePlant');
    const matchesSearch = (
      (eq.equipment_code || '').toLowerCase().includes(term) ||
      (eq.equipment_name || '').toLowerCase().includes(term)
    );
    const matchesFilter = productionLineFilter === 'All' || normalizeEquipmentLineTags(eq).includes(productionLineFilter);
    const matchesPlant = matchesPlantScope(eq.unit_plant || eq.scope_plant_code, activePlant);
    const matchesTrain = trainFilter === 'All' || (eq.linked_trains || []).some(t => String(t.train_id) === String(trainFilter));
    const status = normalizeDisplayStatus(eq.status);
    const matchesStatus = statusCard === 'All'
      || (statusCard === 'Approved' && status === EQUIPMENT_STATUS.APPROVED)
      || (statusCard === 'Pending' && status === EQUIPMENT_STATUS.PENDING_APPROVAL)
      || (statusCard === 'Draft' && status === EQUIPMENT_STATUS.DRAFT);
    return matchesSearch && matchesFilter && matchesPlant && matchesTrain && matchesStatus;
  });

  const displayedAreaTotals = useMemo(() => {
    const filteredIds = new Set(sortedEquipment.map((eq) => eq.equipment_id));
    const selectedFilteredIds = selectedEquipment.filter((id) => filteredIds.has(id));
    const sourceIds = selectedFilteredIds.length ? selectedFilteredIds : Array.from(filteredIds);

    return sourceIds.reduce((acc, equipmentId) => {
      const area = equipmentAreaTotals.byEquipmentId[equipmentId] || { direct: 0, indirect: 0, total: 0 };
      return {
        direct: acc.direct + area.direct,
        indirect: acc.indirect + area.indirect,
        total: acc.total + area.total
      };
    }, { direct: 0, indirect: 0, total: 0 });
  }, [sortedEquipment, selectedEquipment, equipmentAreaTotals]);

  const equipmentCurrentItems = sortedEquipment.slice(
    (equipmentCurrentPage - 1) * itemsPerPage,
    equipmentCurrentPage * itemsPerPage
  );

  const handleHeaderSort = (field) => {
    if (equipmentSortBy === field) {
      setEquipmentSortOrder(equipmentSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setEquipmentSortBy(field);
      setEquipmentSortOrder('asc');
    }
  };

  const renderSortIndicator = (field) => {
    const isActive = equipmentSortBy === field;
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '6px', lineHeight: 0.75, verticalAlign: 'middle' }}>
        <span style={{ color: isActive && equipmentSortOrder === 'asc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)' }}>{'\u25B2'}</span>
        <span style={{ color: isActive && equipmentSortOrder === 'desc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)', marginTop: '-2px' }}>{'\u25BC'}</span>
      </span>
    );
  };

  const handleSelectAll = () => {
    if (selectedEquipment.length === sortedEquipment.length) {
      setSelectedEquipment([]);
    } else {
      setSelectedEquipment(sortedEquipment.map(e => e.equipment_id));
    }
  };

  const handleSelectEquipment = (id) => {
    setSelectedEquipment(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleEquipmentRowClick = (equipmentRow) => {
    setSelectedEquipmentRow(equipmentRow?.equipment_id || null);
  };


  const getExportContextDisplay = (value, fallback) => {
    const normalized = String(value || '').trim();
    if (!normalized) return fallback;
    if (normalized.toLowerCase() === 'all') return fallback;
    return normalized;
  };

  const getExportMetadata = (recordCount, rowCount) => {
    let savedUser = null;
    try {
      const userRaw = localStorage.getItem('user');
      savedUser = userRaw ? JSON.parse(userRaw) : null;
    } catch (error) {
      savedUser = null;
    }

    const exportedAt = new Date();
    const exportedBy =
      savedUser?.username ||
      savedUser?.full_name ||
      savedUser?.name ||
      savedUser?.employee_id ||
      'Unknown User';
    const exportedByRole = savedUser?.role || 'Unknown Role';
    const systemName = 'PrimeClean';
    const systemHost = window?.location?.origin || 'Unknown Origin';
    const systemNode = window?.location?.hostname || 'Unknown Host';
    const unitName = getExportContextDisplay(localStorage.getItem('activePlant'), 'N/A');
    const productionLine = getExportContextDisplay(localStorage.getItem('activeLine'), 'All Sections');
    const selectedTrain = trains.find(t => String(t.train_id) === String(trainFilter));
    const equipmentTrain = trainFilter === 'All' ? 'All Trains' : (selectedTrain ? `${getTrainCodeLabel(selectedTrain)} - ${selectedTrain.train_name || 'Unnamed Train'}` : 'All Trains');

    return {
      reportId: `EQ-EXP-${exportedAt.getTime()}`,
      exportedAt,
      exportedAtDisplay: exportedAt.toLocaleString(),
      exportedAtIso: exportedAt.toISOString(),
      exportedBy,
      exportedByRole,
      systemName,
      systemHost,
      systemNode,
      unitName,
      productionLine,
      equipmentTrain,
      recordCount,
      rowCount
    };
  };

  const getFileNamesForExport = (value) => {
    return parseFiles(value)
      .map((file) => {
        if (typeof file === 'string') return file;
        if (file && typeof file === 'object' && file.name) return file.name;
        return '';
      })
      .filter(Boolean)
      .join(' | ');
  };

  const getEquipmentExportRows = (sourceEquipment) => {
    const rows = [];
    sourceEquipment.forEach((eq) => {
      const area = equipmentAreaTotals.byEquipmentId[eq.equipment_id] || { direct: 0, indirect: 0, total: 0 };
      const eqParts = equipmentParts.filter((part) => part.equipment_id === eq.equipment_id);
      const baseCols = {
        'Equipment Code': eq.equipment_code || '',
        'Equipment Name': eq.equipment_name || '',
        'Equipment Type': eq.equipment_type || '',
        'Section': getEquipmentProductionLine(eq),
        'Equipment Dedicated': eq.is_dedicated ? 'Yes' : 'No',
        'Equipment Shared': eq.is_shared ? 'Yes' : 'No',
        'Equipment Status': normalizeDisplayStatus(eq.status),
        'Reference Document': eq.reference_document || '',
        'Equipment Notes': eq.equipment_notes || '',
        'Equipment Files': getFileNamesForExport(eq.attached_files || eq.attachedFiles),
        'Direct Area': area.direct.toFixed(2),
        'Indirect Area': area.indirect.toFixed(2),
        'Total Area': area.total.toFixed(2),
        'Created At': eq.created_at ? new Date(eq.created_at).toLocaleString() : '',
        'Updated At': eq.updated_at ? new Date(eq.updated_at).toLocaleString() : '',
        'Updated By': eq.updated_by_user || eq.created_by_user || ''
      };

      if (!eqParts.length) {
        rows.push({
          ...baseCols,
          'Part Sequence': '',
          'Part ID': '',
          'Part Code': '',
          'Part Name': '',
          'Contact Type': '',
          'Cleaning Method': '',
          'Part Dedicated': '',
          'Part Shared': '',
          'Material of Construction': '',
          'Surface Area': '',
          'Surface Area Unit': '',
          'Part Files': ''
        });
        return;
      }

      eqParts.forEach((part, idx) => {
        rows.push({
          ...baseCols,
          'Part Sequence': idx + 1,
          'Part ID': part.equipment_part_id || '',
          'Part Code': part.part_code || '',
          'Part Name': part.part_name || '',
          'Contact Type': normalizeContactType(part.contact_type),
          'Cleaning Method': part.cleaning_method || 'Manual',
          'Part Dedicated': (part.is_dedicated === true || Number(part.is_dedicated) === 1) ? 'Yes' : 'No',
          'Part Shared': (part.is_shared === true || Number(part.is_shared) === 1) ? 'Yes' : 'No',
          'Material of Construction': part.material_of_construction || '',
          'Surface Area': part.surface_area || '',
          'Surface Area Unit': formatSurfaceAreaUnitLabel(part.surface_area_unit),
          'Part Files': getFileNamesForExport(part.part_attachments)
        });
      });
    });
    return rows;
  };

  const drawPdfGridTable = (doc, {
    headers,
    rows,
    startY,
    topMargin,
    bottomMargin,
    leftMargin,
    rightMargin,
    headerTextColor,
    bodyTextColor,
    alternateRowColor,
    fontSize,
    colWidthWeights,
    drawHeaderFooter
  }) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableWidth = pageWidth - leftMargin - rightMargin;
    const colCount = headers.length;
    const normalizedWeights = Array.isArray(colWidthWeights) && colWidthWeights.length === colCount
      ? colWidthWeights.map((weight) => {
          const parsed = Number(weight);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
        })
      : Array.from({ length: colCount }, () => 1);
    const totalWeight = normalizedWeights.reduce((sum, weight) => sum + weight, 0) || colCount;
    const colWidths = normalizedWeights.map((weight) => (tableWidth * weight) / totalWeight);
    const cellPadding = 3;
    const lineHeight = Math.max(8, fontSize + 2);
    const minRowHeight = Math.max(14, lineHeight + 2);
    const headerFontSize = Math.max(fontSize + 0.6, 6.4);
    const headerLineHeight = Math.max(8, headerFontSize + 1.8);
    const originalFontSize = typeof doc.getFontSize === 'function' ? doc.getFontSize() : fontSize;
    doc.setFontSize(headerFontSize);
    const wrappedHeaderCells = headers.map((header, index) =>
      doc
        .splitTextToSize(String(header || '').trim(), Math.max(colWidths[index] - (cellPadding * 2), 10))
        .map((line) => String(line || '').trim())
        .filter(Boolean)
    );
    doc.setFontSize(originalFontSize);
    const maxHeaderLines = wrappedHeaderCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
    const headerHeight = Math.max(minRowHeight + 4, (maxHeaderLines * headerLineHeight) + (cellPadding * 2) + 2);
    let y = startY;

    const drawHeaderRow = () => {
      doc.setFillColor(255, 255, 255);
      doc.rect(leftMargin, y, tableWidth, headerHeight, 'F');
      doc.setDrawColor(180, 192, 208);
      doc.setTextColor(...headerTextColor);
      doc.setFontSize(headerFontSize);
      doc.setFont('helvetica', 'bold');
      let x = leftMargin;
      headers.forEach((_, index) => {
        doc.rect(x, y, colWidths[index], headerHeight, 'S');
        const lineArray = Array.isArray(wrappedHeaderCells[index]) && wrappedHeaderCells[index].length > 0
          ? wrappedHeaderCells[index]
          : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + headerFontSize + (lineIndex * headerLineHeight);
          if (textY <= y + headerHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });
      doc.setFont('helvetica', 'normal');
      y += headerHeight;
    };

    const addNewPageWithHeader = () => {
      doc.addPage();
      if (typeof drawHeaderFooter === 'function') drawHeaderFooter();
      y = topMargin;
      drawHeaderRow();
    };

    if (y + headerHeight > pageHeight - bottomMargin) {
      addNewPageWithHeader();
    } else {
      drawHeaderRow();
    }

    rows.forEach((row, rowIndex) => {
      const wrappedCells = row.map((cell, index) => {
        const cellText = String(cell ?? '-');
        return doc.splitTextToSize(cellText, colWidths[index] - (cellPadding * 2));
      });
      const maxLines = wrappedCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
      const rowHeight = Math.max(minRowHeight, (maxLines * lineHeight) + (cellPadding * 2));

      if (y + rowHeight > pageHeight - bottomMargin) {
        addNewPageWithHeader();
      }

      if (rowIndex % 2 === 1) {
        doc.setFillColor(...alternateRowColor);
        doc.rect(leftMargin, y, tableWidth, rowHeight, 'F');
      }

      doc.setTextColor(...bodyTextColor);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);

      let x = leftMargin;
      wrappedCells.forEach((lines, index) => {
        doc.setDrawColor(203, 213, 225);
        doc.rect(x, y, colWidths[index], rowHeight);

        const lineArray = Array.isArray(lines) && lines.length > 0 ? lines : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + fontSize + (lineIndex * lineHeight);
          if (textY <= y + rowHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });

      y += rowHeight;
    });
  };

  const exportEquipmentToExcel = () => {
    if (sortedEquipment.length === 0) {
      toast.info('No equipment available to export');
      return;
    }

    const rows = getEquipmentExportRows(sortedEquipment);
    const metadata = getExportMetadata(sortedEquipment.length, rows.length);
    const metadataRows = [
      ['PrimeClean Equipment Master Export'],
      ['21 CFR Part 11 Compliance Context'],
      [],
      ['Report ID', metadata.reportId],
      ['Report Name', 'Equipment Master'],
      ['Unit Name', metadata.unitName],
      ['Section', metadata.productionLine],
      ['Equipment Train', metadata.equipmentTrain],
      ['Exported At', metadata.exportedAtDisplay],
      ['Exported At (ISO)', metadata.exportedAtIso],
      ['Exported By', metadata.exportedBy],
      ['Exported By Role', metadata.exportedByRole],
      ['System Name', metadata.systemName],
      ['System Node', metadata.systemNode],
      ['System Origin', metadata.systemHost],
      ['Equipment Count', metadata.recordCount],
      ['Equipment/Parts Rows', metadata.rowCount],
      ['Note', 'Electronic record export with trace metadata for audit review.']
    ];

    const ws = XLSX.utils.aoa_to_sheet(metadataRows);
    const tableStartRow = metadataRows.length + 2;
    XLSX.utils.sheet_add_json(ws, rows, { origin: `A${tableStartRow}`, skipHeader: false });
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: tableStartRow - 1, c: 0 },
        e: { r: tableStartRow + rows.length - 1, c: Object.keys(rows[0] || {}).length - 1 }
      })
    };
    ws['!cols'] = Object.keys(rows[0] || {}).map((key) => ({ wch: Math.min(Math.max(String(key).length + 6, 14), 40) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment Details');
    const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(wb, `equipment_master_${fileStamp}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const printEquipmentDetails = () => {
    const content = document.getElementById('equipment-details-print');
    if (!content) {
      toast.error('Unable to find content to print');
      return;
    }
    
    openPrintPreview({
      reportKey: 'equipment_master',
      contentHTML: content.innerHTML,
      title: `${viewingEquipment?.equipment_name || 'Equipment'} Details`,
      documentName: 'Equipment Master Details',
      clientName: reportSettings?.report_client_name || 'Client Name',
      clientAddress: unitReportAddress(reportSettings) || 'Client Address',
      clientLogoUrl: reportSettings?.report_client_logo || '',
      primeCleanLogoUrl: reportSettings?.report_primeclean_logo || '',
      revisionNo: reportSettings?.report_document_rev_no || '01',
      formatNo: reportSettings?.report_format_no_equipment_master || '',
      unitName: getActivePrintUnitName(),
      timestamp: new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    });
  };

  const exportEquipmentToPDF = async () => {
    // Header/footer from Settings → App Configuration → Reports & Documents.
    const reportSettings = withReportProfile('equipment_master', 'Equipment Master Report');
    if (sortedEquipment.length === 0) {
      toast.info('No equipment available to export');
      return;
    }

    try {
      const rows = getEquipmentExportRows(sortedEquipment);
      const metadata = getExportMetadata(sortedEquipment.length, rows.length);
      const [clientLogoData, primeLogoData] = await Promise.all([
        toDataUrlIfPossible(reportSettings.report_client_logo),
        toDataUrlIfPossible(reportSettings.report_primeclean_logo)
      ]);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const headerTop = 16;
      const headerRowSplit = 44;
      const headerBottom = 82;
      const leftLogoWidth = 130;
      const rightLogoWidth = 130;
      const bodyTop = headerBottom + 8;
      const footerTop = pageHeight - 42;

      const truncatePdfHeaderText = (value, maxWidth) => {
        const text = String(value || '').trim();
        if (!text) return '-';
        if (doc.getTextWidth(text) <= maxWidth) return text;
        let truncated = text;
        while (truncated.length > 0 && doc.getTextWidth(`${truncated}...`) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated ? `${truncated}...` : '...';
      };

      const drawHeaderFooter = () => {
        const pageInfo = doc.internal.getCurrentPageInfo ? doc.internal.getCurrentPageInfo() : null;
        const pageNo = pageInfo?.pageNumber || doc.internal.getNumberOfPages();
        const totalPages = doc.internal.getNumberOfPages();

        doc.setDrawColor(80, 80, 80);
        doc.rect(20, headerTop, pageWidth - 40, headerBottom - headerTop);
        doc.line(20 + leftLogoWidth, headerTop, 20 + leftLogoWidth, headerRowSplit);
        doc.line(pageWidth - 20 - rightLogoWidth, headerTop, pageWidth - 20 - rightLogoWidth, headerRowSplit);
        doc.line(20, headerRowSplit, pageWidth - 20, headerRowSplit);

        if (clientLogoData) {
          doc.addImage(clientLogoData, 'PNG', 26, headerTop + 7, leftLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('Client Logo', 26, headerTop + 19);
        }

        if (primeLogoData) {
          doc.addImage(primeLogoData, 'PNG', pageWidth - 20 - rightLogoWidth + 6, headerTop + 7, rightLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('PrimeClean Logo', pageWidth - 20 - rightLogoWidth + 8, headerTop + 19);
        }

        doc.setFontSize(8.6);
        doc.setTextColor(15, 23, 42);
        doc.text(reportSettings.report_client_name || 'Client Name', pageWidth / 2, headerTop + 16, { align: 'center' });
        doc.setFontSize(7.6);
        doc.text(unitReportAddress(reportSettings) || 'Client Address', pageWidth / 2, headerTop + 27, { align: 'center' });

        doc.setFontSize(8.5);
        doc.text(
          truncatePdfHeaderText(`Document Name: ${reportSettings.report_document_name || 'Equipment Master Report'}`, 220),
          26,
          headerRowSplit + 14
        );
        doc.text(
          truncatePdfHeaderText(`Unit Name: ${metadata.unitName}`, 210),
          pageWidth / 2,
          headerRowSplit + 14,
          { align: 'center' }
        );
        doc.text(`Effective Date: ${metadata.exportedAtDisplay}`, pageWidth - 24, headerRowSplit + 14, { align: 'right' });
        doc.text(
          truncatePdfHeaderText(`Section: ${metadata.productionLine}  |  Equipment Train: ${metadata.equipmentTrain}`, 260),
          26,
          headerRowSplit + 26
        );
        doc.text(
          truncatePdfHeaderText(`Document & Rev. No.: ${reportSettings.report_document_rev_no || '01'}`, 210),
          pageWidth / 2,
          headerRowSplit + 26,
          { align: 'center' }
        );

        // Footer
        doc.line(20, footerTop, pageWidth - 20, footerTop);
        doc.setFontSize(7.2);
        doc.text(
          `Report ID: ${metadata.reportId}  Generated By: ${metadata.exportedBy} (${metadata.exportedByRole})  System: ${metadata.systemName} | ${metadata.systemNode}`,
          pageWidth / 2,
          pageHeight - 27,
          { align: 'center' }
        );
        if (reportSettings.report_format_no_equipment_master) {
          doc.text(`Format No: ${reportSettings.report_format_no_equipment_master}`, pageWidth / 2, pageHeight - 19, { align: 'center' });
        }
        doc.setFontSize(8);
        doc.text(`Page ${pageNo} of ${totalPages}`, 24, pageHeight - 12);
        doc.text(`Print: ${metadata.exportedAtDisplay}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
        doc.text(reportSettings.report_footer_tagline || 'Data is generated and handled by PrimeClean.', pageWidth - 24, pageHeight - 12, { align: 'right' });
      };

      const pdfHeaders = Object.keys(rows[0] || {});
      const pdfBodyRows = rows.map((row) => pdfHeaders.map((header) => row[header]));

      drawHeaderFooter();
      drawPdfGridTable(doc, {
        headers: pdfHeaders,
        rows: pdfBodyRows,
        startY: bodyTop + 4,
        topMargin: bodyTop,
        bottomMargin: 40,
        leftMargin: 20,
        rightMargin: 20,
        headerTextColor: [0, 0, 0],
        bodyTextColor: [15, 23, 42],
        alternateRowColor: [248, 250, 252],
        fontSize: 5.8,
        colWidthWeights: pdfHeaders.map(() => 1),
        drawHeaderFooter
      });

      const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
      doc.save(`equipment_master_${fileStamp}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  const downloadEquipmentImportTemplate = () => {
    const templateRows = [
      {
        equipment_code: 'EQ-001',
        equipment_name: 'Tablet Press Machine',
        equipment_type: 'Processing',
        production_line: 'Tablet Line',
        equipment_dedicated: 'Yes',
        equipment_shared: 'No',
        equipment_status: 'Draft',
        reference_document: 'DOC-EQ-001',
        equipment_notes: 'Primary compression equipment',
        equipment_files: '',
        part_code: 'EQ-001-P01',
        part_name: 'Product Contact Surface',
        contact_type: 'Direct',
        cleaning_method: 'Manual',
        part_dedicated: 'Yes',
        part_shared: 'No',
        material_of_construction: 'SS 316L',
        surface_area: 5000,
        surface_area_unit: 'cm2',
        part_files: ''
      },
      {
        equipment_code: 'EQ-001',
        equipment_name: 'Tablet Press Machine',
        equipment_type: 'Processing',
        production_line: 'Tablet Line',
        equipment_dedicated: 'Yes',
        equipment_shared: 'No',
        equipment_status: 'Draft',
        reference_document: 'DOC-EQ-001',
        equipment_notes: 'Primary compression equipment',
        equipment_files: '',
        part_code: 'EQ-001-P02',
        part_name: 'Secondary Contact Surface',
        contact_type: 'Indirect',
        cleaning_method: 'Manual',
        part_dedicated: 'No',
        part_shared: 'Yes',
        material_of_construction: 'SS 316L',
        surface_area: 2500,
        surface_area_unit: 'cm2',
        part_files: ''
      },
      {
        equipment_code: 'EQ-002',
        equipment_name: 'Coating Pan',
        equipment_type: 'Processing',
        production_line: 'Tablet Line',
        equipment_dedicated: 'No',
        equipment_shared: 'Yes',
        equipment_status: 'Draft',
        reference_document: 'DOC-EQ-002',
        equipment_notes: 'Shared coating line equipment',
        equipment_files: '',
        part_code: 'EQ-002-P01',
        part_name: 'Coating Contact Surface',
        contact_type: 'Direct',
        cleaning_method: 'Manual',
        part_dedicated: 'No',
        part_shared: 'Yes',
        material_of_construction: 'SS 316L',
        surface_area: 1800,
        surface_area_unit: 'cm2',
        part_files: ''
      }
    ];

    const instructions = [
      ['Equipment Import Template (Inlined Format)'],
      [],
      ['Fill one Part per row. Equipment with multiple parts should have multiple rows with the SAME Equipment Code.'],
      ['The importer will automatically group all rows sharing the same Equipment Code into a single Equipment.'],
      ['Imported equipments are saved as Drafts by default.'],
      ['Do not change header names in the Equipment sheet.']
    ];

    const wsTemplate = XLSX.utils.json_to_sheet(templateRows);
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);

    wsTemplate['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: templateRows.length, c: Object.keys(templateRows[0]).length - 1 }
      })
    };
    wsTemplate['!cols'] = Object.keys(templateRows[0]).map((key) => ({ wch: Math.min(Math.max(key.length + 4, 16), 32) }));
    wsInstructions['!cols'] = [{ wch: 90 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsTemplate, 'Equipment Details');
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Read Me');
    XLSX.writeFile(wb, 'equipment_import_template.xlsx');
    toast.success('Import template downloaded');
  };

  const normalizeImportRow = (row = {}) => {
    return Object.entries(row).reduce((acc, [key, value]) => {
      const rawKey = String(key || '').trim().toLowerCase();
      if (!rawKey) return acc;
      acc[rawKey] = value;
      const sanitizedKey = rawKey.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      if (sanitizedKey && acc[sanitizedKey] === undefined) {
        acc[sanitizedKey] = value;
      }
      return acc;
    }, {});
  };

  const normalizeImportRows = (rows = []) => rows.map((row) => normalizeImportRow(row));

  const parseYesNo = (value) => {
    if (typeof value === 'boolean') return value;
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return false;
    return ['yes', 'y', 'true', '1'].includes(normalized);
  };

  const parseImportFiles = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (error) {
        // fallthrough to split
      }
      return trimmed.split(/[|;]/).map((item) => item.trim()).filter(Boolean);
    }
    return [];
  };

  const getRowValue = (row, keys = []) => {
    for (const key of keys) {
      if (!key) continue;
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }
    return '';
  };

  // Import is a bulk state-changing action: collect the e-signature first, then run.
  const handleEquipmentFileImport = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPendingEquipmentImport(file);
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const runEquipmentImport = async (file) => {
    try {
      setEquipmentLoading(true);
      const rawRows = await (file.name.toLowerCase().endsWith('.csv')
        ? parseCSV(file)
        : parseExcel(file)
      );

      if (!Array.isArray(rawRows) || rawRows.length === 0) {
        toast.error('No import rows found in file');
        return;
      }

      const rows = normalizeImportRows(rawRows);
      const groups = new Map();

      rows.forEach((row) => {
        const equipmentCode = String(getRowValue(row, ['equipment_code'])).trim();
        const equipmentName = String(getRowValue(row, ['equipment_name'])).trim();
        if (!equipmentCode && !equipmentName) return;
        const key = equipmentCode || equipmentName;
        if (!groups.has(key)) {
          groups.set(key, { rows: [] });
        }
        groups.get(key).rows.push(row);
      });

      if (groups.size === 0) {
        toast.error('No equipment rows found in file');
        return;
      }

      let successCount = 0;
      const failures = [];

      for (const [key, group] of groups.entries()) {
        try {
          const base = group.rows[0] || {};
          const equipmentCode = String(getRowValue(base, ['equipment_code'])).trim();
          const equipmentName = String(getRowValue(base, ['equipment_name'])).trim();
          if (!equipmentCode || !equipmentName) {
            throw new Error('Equipment Code and Name are required');
          }

          const payload = {
            equipment_code: equipmentCode,
            equipment_name: equipmentName,
            equipment_type: String(getRowValue(base, ['equipment_type'])).trim() || 'Processing',
            production_line: String(getRowValue(base, ['production_line'])).trim(),
            unit_plant: String(getRowValue(base, ['unit_plant', 'unit'])).trim() || (activePlantFilter && activePlantFilter !== 'All' ? activePlantFilter : ''),
            esign_password: eSignData.password,
            esign_comment: eSignData.comment,
            is_dedicated: parseYesNo(getRowValue(base, ['equipment_dedicated', 'is_dedicated', 'dedicated'])) ? 1 : 0,
            is_shared: parseYesNo(getRowValue(base, ['equipment_shared', 'is_shared', 'shared'])) ? 1 : 0,
            reference_document: String(getRowValue(base, ['reference_document'])).trim(),
            equipment_notes: String(getRowValue(base, ['equipment_notes'])).trim(),
            attached_files: parseImportFiles(getRowValue(base, ['equipment_files', 'attached_files', 'attached_files'])),
            status: EQUIPMENT_STATUS.DRAFT
          };

          const res = await equipmentAPI.create(payload);
          const eqId = res?.data?.equipment_id || res?.data?.data?.equipment_id;

          if (eqId) {
            for (const row of group.rows) {
              const partCode = String(getRowValue(row, ['part_code'])).trim();
              const partName = String(getRowValue(row, ['part_name'])).trim();
              if (!partCode && !partName) continue;

              await equipmentAPI.createPart({
                equipment_id: eqId,
                part_code: partCode,
                part_name: partName,
                contact_type: String(getRowValue(row, ['contact_type'])).trim() || 'Direct',
                cleaning_method: String(getRowValue(row, ['cleaning_method'])).trim() || 'Manual',
                is_dedicated: parseYesNo(getRowValue(row, ['part_dedicated', 'part_is_dedicated'])) ? 1 : 0,
                is_shared: parseYesNo(getRowValue(row, ['part_shared', 'part_is_shared'])) ? 1 : 0,
                material_of_construction: String(getRowValue(row, ['material_of_construction'])).trim(),
                surface_area: getRowValue(row, ['surface_area']) === '' ? null : getRowValue(row, ['surface_area']),
                surface_area_unit: String(getRowValue(row, ['surface_area_unit'])).trim() || 'cm2',
                part_attachments: parseImportFiles(getRowValue(row, ['part_files', 'part_attachments']))
              });
            }
          }

          successCount += 1;
        } catch (error) {
          failures.push(`${key}: ${error?.message || 'Import failed'}`);
        }
      }

      if (successCount > 0) {
        toast.success(`Imported ${successCount} equipment record(s)`);
        loadEquipmentData();
      }
      if (failures.length > 0) {
        toast.warn(`Failed ${failures.length}. ${failures.slice(0, 3).join(' | ')}`);
      }
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setEquipmentLoading(false);
    }
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => resolve(results.data || []),
        error: (error) => reject(error)
      });
    });
  };

  const parseExcel = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const wb = XLSX.read(evt.target.result, { type: 'binary' });
          const preferredSheet =
            wb.SheetNames.find((name) => String(name).trim().toLowerCase() === 'equipment details') ||
            wb.SheetNames.find((name) => String(name).trim().toLowerCase() === 'equipment') ||
            wb.SheetNames[0];
          const ws = wb.Sheets[preferredSheet];
          const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false });
          const headerIndex = grid.findIndex((cells) => cells.some((cell) =>
            String(cell || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_') === 'equipment_code'
          ));
          if (headerIndex < 0) throw new Error('Could not find the Equipment Code header in this worksheet');
          const headers = grid[headerIndex].map((cell) => String(cell || '').trim());
          const data = grid.slice(headerIndex + 1).map((cells) => headers.reduce((row, header, index) => {
            if (header) row[header] = cells[index] ?? '';
            return row;
          }, {}));
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error reading Excel file'));
      reader.readAsBinaryString(file);
    });
  };

  const isDraftESignMode = eSignMode === 'draft';
  const loading = equipmentLoading;

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 className="page-title">Equipment Master</h2>
            <p className="page-subtitle">Equipment inventory, parts, cleaning-contact classification and train linkage per production line.</p>
          </div>
          <div className="toolbar-full" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {isCurrentUserSuperAdmin && selectedEquipment.length > 0 && (
              <button className="btn btn-danger" onClick={handleBulkDelete}>
                <FaTrash /> Deactivate Selected ({selectedEquipment.length})
              </button>
            )}
            {canBulkProductApprove && bulkEquipmentApproveEligible.length > 0 && (
              <button className="btn btn-success" onClick={() => handleBulkEquipmentApproval('approval', 'standard')}>
                <FaCheck /> Approve Selected ({bulkEquipmentApproveEligible.length})
              </button>
            )}
            {canBulkProductApprove && bulkEquipmentDeleteProductEligible.length > 0 && (
              <button className="btn btn-info" onClick={() => handleBulkEquipmentApproval('product', 'delete')}>
                <FaCheck /> Approve Deact-P ({bulkEquipmentDeleteProductEligible.length})
              </button>
            )}
            {canBulkQaApprove && bulkEquipmentDeleteQaEligible.length > 0 && (
              <button className="btn btn-warning" onClick={() => handleBulkEquipmentApproval('qa', 'delete')}>
                <FaCheck /> Approve Deact-Q ({bulkEquipmentDeleteQaEligible.length})
              </button>
            )}
            <button className="btn btn-success" onClick={exportEquipmentToExcel}>
              <FaDownload /> Excel
            </button>
            <button className="btn btn-danger" onClick={exportEquipmentToPDF}>
              <FaDownload /> PDF
            </button>
            <button className="btn btn-outline" onClick={downloadEquipmentImportTemplate}>
              <FaDownload /> Import Template
            </button>
            <label className="btn btn-info" style={{ margin: 0, cursor: 'pointer' }}>
              <FaUpload /> Import
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleEquipmentFileImport} style={{ display: 'none' }} />
            </label>
            <button className="btn btn-primary" onClick={handleEquipmentAdd}>
              <FaTools /> Add Equipment
            </button>
          </div>

          <div className="toolbar-compact" style={{ gap: '10px' }}>
            <button className="btn btn-primary" onClick={handleEquipmentAdd}>
              <FaTools /> Add Equipment
            </button>
            <ActionsMenu
              title="More actions"
              items={[
                { label: `Deactivate Selected (${selectedEquipment.length})`, icon: <FaTrash />, danger: true, show: isCurrentUserSuperAdmin && selectedEquipment.length > 0, onClick: handleBulkDelete },
                { label: `Approve Selected (${bulkEquipmentApproveEligible.length})`, icon: <FaCheck />, show: canBulkProductApprove && bulkEquipmentApproveEligible.length > 0, onClick: () => handleBulkEquipmentApproval('approval', 'standard') },
                { label: `Approve Deact-P (${bulkEquipmentDeleteProductEligible.length})`, icon: <FaCheck />, show: canBulkProductApprove && bulkEquipmentDeleteProductEligible.length > 0, onClick: () => handleBulkEquipmentApproval('product', 'delete') },
                { label: `Approve Deact-Q (${bulkEquipmentDeleteQaEligible.length})`, icon: <FaCheck />, show: canBulkQaApprove && bulkEquipmentDeleteQaEligible.length > 0, onClick: () => handleBulkEquipmentApproval('qa', 'delete') },
                { divider: true },
                { label: 'Export Excel', icon: <FaDownload />, onClick: exportEquipmentToExcel },
                { label: 'Export PDF', icon: <FaDownload />, onClick: exportEquipmentToPDF },
                { label: 'Import Template', icon: <FaDownload />, onClick: downloadEquipmentImportTemplate },
                { label: 'Import', icon: <FaUpload />, onClick: () => mobileImportInputRef.current?.click() },
              ]}
            />
            <input ref={mobileImportInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleEquipmentFileImport} style={{ display: 'none' }} />
          </div>
        </div>

        <div className="stat-card-grid">
          {[
            { key: 'All', title: 'Total Equipment', value: equipment.length, icon: FaTools, color: 'primary' },
            { key: 'Approved', title: 'Approved', value: equipment.filter((e) => normalizeDisplayStatus(e.status) === EQUIPMENT_STATUS.APPROVED).length, icon: FaCheck, color: 'success' },
            { key: 'Pending', title: 'Pending Approval', value: equipment.filter((e) => normalizeDisplayStatus(e.status) === EQUIPMENT_STATUS.PENDING_APPROVAL).length, icon: FaHourglassHalf, color: 'warning' },
            { key: 'Draft', title: 'Draft', value: equipment.filter((e) => normalizeDisplayStatus(e.status) === EQUIPMENT_STATUS.DRAFT).length, icon: FaFileAlt, color: 'info' },
          ].map((c) => (
            <StatCard
              key={c.key} compact filterHint title={c.title} value={c.value} icon={c.icon} color={c.color}
              active={statusCard === c.key}
              onClick={() => { setStatusCard(c.key === 'All' || statusCard === c.key ? 'All' : c.key); setEquipmentCurrentPage(1); }}
            />
          ))}
          <StatCard compact title="Parts" value={equipmentParts.length} icon={FaCogs} color="info" />
        </div>

        {/* Search & Filter Bar */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 200px', minWidth: '160px', position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by code or name..."
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore
              data-bitwarden-ignore
              value={equipmentSearchTerm}
              onChange={(e) => setEquipmentSearchTerm(e.target.value)}
              style={{ paddingLeft: '30px' }}
            />
          </div>
          {productionLineFilter && productionLineFilter !== 'All' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
              <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Section:</span>
              <span style={{ padding: '4px 10px', borderRadius: '10px', background: 'var(--success-color, #10b981)', color: '#fff', fontSize: 'var(--fs-md)', fontWeight: 600, whiteSpace: 'nowrap' }}>{productionLineFilter}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
            <label style={{ margin: 0, whiteSpace: 'nowrap', fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Equipment Train:</label>
            <select className="form-control form-control-sm" value={trainFilter} onChange={(e) => setTrainFilter(e.target.value)} style={{ minWidth: '180px', width: 'auto' }}>
              <option value="All">All Trains</option>
              {linkedTrainOptions.map((t) => (
                <option key={t.train_id} value={String(t.train_id)}>{getTrainCodeLabel(t)} - {t.train_name || 'Unnamed Train'}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedEquipment.length > 0 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '12px' }}>
              <div style={{ border: '1px solid var(--info-border, #dbeafe)', backgroundColor: 'var(--info-bg, #eff6ff)', borderRadius: '8px', padding: '10px 12px' }}>
                <small style={{ color: 'var(--info-text, #1e40af)', fontWeight: 600 }}>Selected Direct Area (cm{"\u00B2"})</small>
                <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--primary-color)' }}>{displayedAreaTotals.direct.toFixed(2)} cm{"\u00B2"}</div>
              </div>
              <div style={{ border: '1px solid var(--success-border, #dcfce7)', backgroundColor: 'var(--success-bg, #f0fdf4)', borderRadius: '8px', padding: '10px 12px' }}>
                <small style={{ color: 'var(--success-text, #166534)', fontWeight: 600 }}>Selected Indirect Area (cm{"\u00B2"})</small>
                <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--success-color)' }}>{displayedAreaTotals.indirect.toFixed(2)} cm{"\u00B2"}</div>
              </div>
              <div style={{ border: '1px solid var(--border-color)', backgroundColor: 'var(--surface-light)', borderRadius: '8px', padding: '10px 12px' }}>
                <small style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Selected Total Area (cm{"\u00B2"})</small>
                <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--text-primary)' }}>{displayedAreaTotals.total.toFixed(2)} cm{"\u00B2"}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setTargetTrainId('');
                  setTargetTrainSearchTerm('');
                  setShowTargetTrainDropdown(false);
                  refreshTrainOptions();
                  setShowAddToTrainModal(true);
                }}
                title="Add selected equipment to an existing train"
              >
                <FaPlus /> Add to Existing Train ({selectedEquipment.length})
              </button>
            </div>
          </>
        )}

        {/* Equipment Table */}
        <FloatingScrollWrapper innerClassName="table-container">
          <table>
            <thead>
              <tr>
                {canUseBulkActions && (
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={sortedEquipment.length > 0 && selectedEquipment.length === sortedEquipment.length}
                    />
                  </th>
                )}
                <th>
                  <button type="button" onClick={() => handleHeaderSort('equipment_code')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                    Equipment Code {renderSortIndicator('equipment_code')}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => handleHeaderSort('equipment_name')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                    Equipment Name {renderSortIndicator('equipment_name')}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => handleHeaderSort('status')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                    Status {renderSortIndicator('status')}
                  </button>
                </th>
                <th>Equipment Parts</th>
                <th>Area (D / I / T, cm²)</th>
                <th style={{ width: '150px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {equipmentCurrentItems.length === 0 ? (
                <tr><td colSpan={canUseBulkActions ? 7 : 6} className="text-center">No equipment found</td></tr>
              ) : (
                equipmentCurrentItems.map((eq) => {
                  const parts = equipmentParts.filter(p => p.equipment_id === eq.equipment_id);
                  const area = equipmentAreaTotals.byEquipmentId[eq.equipment_id] || { direct: 0, indirect: 0, total: 0 };
                  const statusInfo = getEquipmentStatusInfo(eq);
                  return (
                    <tr
                      key={eq.equipment_id}
                      onClick={() => handleEquipmentRowClick(eq)}
                      onDoubleClick={() => handleEquipmentView(eq)}
                      className={selectedEquipmentRow === eq.equipment_id ? 'selected' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      {canUseBulkActions && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedEquipment.includes(eq.equipment_id)}
                            onChange={() => handleSelectEquipment(eq.equipment_id)}
                          />
                        </td>
                      )}
                      <td><span className="record-link" onClick={() => handleEquipmentView(eq)} title="View equipment">{eq.equipment_code}</span></td>
                      <td>{eq.equipment_name}</td>
                      <td>
                          <span className={`badge ${statusInfo.badgeClass}`} title={statusInfo.tooltip}>
                            {statusInfo.text}
                          </span>
                        </td>
                      <td><small>{parts.length} parts</small></td>
                      <td><small>{area.direct.toFixed(2)} / {area.indirect.toFixed(2)} / {area.total.toFixed(2)}</small></td>
                      <td className="action-cell" style={{ width: '150px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          {normalizeDisplayStatus(eq.status) === EQUIPMENT_STATUS.PENDING_APPROVAL && (
                            <button className="btn btn-sm btn-success" onClick={(e) => { e.stopPropagation(); handleEquipmentStageApproval(eq); }} title="Approve Equipment">
                              <FaCheck />
                            </button>
                          )}
                          <button className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); handleEquipmentView(eq); }} title="View">
                            <FaEye />
                          </button>
                          <ActionsMenu items={[
                            { label: 'Edit', icon: <FaEdit />, onClick: () => handleEquipmentEdit(eq) },
                            { label: 'Copy', icon: <FaCopy />, onClick: () => handleEquipmentDuplicate(eq) },
                            { label: 'Version History', icon: <FaHourglassHalf />, onClick: () => setHistoryModal(eq) },
                            { label: 'Deactivate', icon: <FaTrash />, onClick: () => handleEquipmentDelete(eq), danger: true },
                          ]} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </FloatingScrollWrapper>

        {/* Pagination */}
        <TablePagination total={sortedEquipment.length} page={equipmentCurrentPage} pageSize={itemsPerPage} onPageChange={setEquipmentCurrentPage} onPageSizeChange={(n) => { setItemsPerPage(n); setEquipmentCurrentPage(1); }} itemLabel="equipment" />
      </div>

      {/* Equipment Add/Edit Modal */}
      {showEquipmentModal && (
        <Modal isOpen={showEquipmentModal} size="xl" title={<><FaTools style={{ color: 'var(--warning-color, #f97316)' }} /> {equipmentEditingId ? 'Edit' : 'Add'} Equipment</>} onClose={() => { setShowEquipmentModal(false); resetEquipmentForm(); }}>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' }}>
                  <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ width: '4px', height: '20px', background: 'var(--warning-color, #f97316)', borderRadius: '2px' }}></span>
                    Basic Information
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group">
                      <label className="form-label">Equipment Code *</label>
                      <input className="form-control" value={equipmentFormData.equipment_code} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, equipment_code: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Equipment Name *</label>
                      <input className="form-control" value={equipmentFormData.equipment_name} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, equipment_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Equipment Type</label>
                      <select className="form-control" value={equipmentFormData.equipment_type || 'Processing'} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, equipment_type: e.target.value })}>
                        <option value="Processing">Processing</option>
                        <option value="Packing">Packing</option>
                        <option value="Utility">Utility</option>
                        <option value="Ancillary">Ancillary</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Section</label>
                      <select className="form-control" value={equipmentFormData.production_line || ''} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, production_line: e.target.value })}>
                        <option value="">-- Select Section --</option>
                        {commercialLines.map(l => (
                          <option key={l.line_name} value={l.line_name}>
                            {l.line_name}{l.route_of_admin ? ` (${l.route_of_admin})` : ''}
                          </option>
                        ))}
                        {productionLineFormOptions.filter(pl => !commercialLines.some(cl => cl.line_name === pl)).map(pl => (
                          <option key={pl} value={pl}>{pl}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Linked Equipment Train(s)</span>
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                        {(equipmentFormData.linked_trains || []).map((t, i) => (
                           <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr auto', gap: '8px', alignItems: 'center', background: 'var(--bg-color, #f8fafc)', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                             <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: 'var(--fs-base)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                               {t.train_code || `Train ID: ${t.train_id}`} - {t.train_name}
                             </span>
                             <select 
                               className="form-control" 
                               style={{ height: '32px', fontSize: 'var(--fs-md)', padding: '2px 8px' }}
                               value={t.product_type || ''}
                               onChange={(e) => {
                                 const updated = [...equipmentFormData.linked_trains];
                                 updated[i] = { ...updated[i], product_type: e.target.value };
                                 setEquipmentFormData({ ...equipmentFormData, linked_trains: updated });
                               }}
                             >
                               <option value="">Select Product Type (Optional)</option>
                               <option>Dry Powder Injection</option>
                               <option>Liquid Injection</option>
                               <option>Tablet</option>
                               <option>Capsule</option>
                               <option>Ointment</option>
                               <option>Solution</option>
                               <option>Dry Syrup</option>
                             </select>
                             <button 
                               type="button" 
                               onClick={() => setEquipmentFormData({ ...equipmentFormData, linked_trains: equipmentFormData.linked_trains.filter((_, idx) => idx !== i) })} 
                               style={{ background: 'var(--danger-bg)', border: 'none', color: 'var(--danger-text)', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-xl)', lineHeight: '1' }}
                             >&times;</button>
                           </div>
                        ))}
                      </div>
                      
                      {/* Searchable Train selection */}
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'relative' }}>
                          <FaSearch style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--text-light)', zIndex: 1 }} />
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search and add train (Strictly filtered by Unit & Section)..."
                            style={{ paddingLeft: '35px' }}
                            value={trainSearchTerm}
                            onFocus={() => setShowTrainSearchResults(true)}
                            onChange={(e) => {
                              setTrainSearchTerm(e.target.value);
                              setShowTrainSearchResults(true);
                            }}
                          />
                        </div>
                        {showTrainSearchResults && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                            {(() => {
                              const activePlant = localStorage.getItem('activePlant');
                              const activeLine = localStorage.getItem('activeLine');
                              const filteredTrains = (trains || []).filter(tr => {
                                const matchesPlant = !activePlant || activePlant === 'All' || String(tr.unit_plant || tr.unit_id || '').includes(activePlant);
                                const matchesLine = !activeLine || activeLine === 'All' || String(tr.production_line || tr.line_id || '').includes(activeLine);
                                const matchesSearch = !trainSearchTerm || 
                                  String(tr.train_code || '').toLowerCase().includes(trainSearchTerm.toLowerCase()) || 
                                  String(tr.train_name || '').toLowerCase().includes(trainSearchTerm.toLowerCase());
                                const notAlreadyLinked = !(equipmentFormData.linked_trains || []).some(lt => String(lt.train_id) === String(tr.train_id));
                                return matchesPlant && matchesLine && matchesSearch && notAlreadyLinked;
                              });

                              return (
                                <>
                                  {filteredTrains.length === 0 ? (
                                    <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No matching trains found</div>
                                  ) : (
                                    filteredTrains.map(tr => (
                                      <div
                                        key={tr.train_id}
                                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setEquipmentFormData({
                                            ...equipmentFormData,
                                            linked_trains: [...(equipmentFormData.linked_trains || []), {
                                              train_id: tr.train_id,
                                              train_code: tr.train_code,
                                              train_name: tr.train_name,
                                              product_type: ''
                                            }]
                                          });
                                          setTrainSearchTerm('');
                                          setShowTrainSearchResults(false);
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.06))'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--card-bg)'}
                                      >
                                        <strong>{tr.train_code}</strong> - {tr.train_name}
                                      </div>
                                    ))
                                  )}
                                  <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setShowTrainSearchResults(false);
                                        setTrainSearchTerm('');
                                        navigate('/equipment-trains', { state: { openAddTrainModal: true } });
                                      }}
                                      style={{ display: 'inline-block', color: 'var(--info-text, #1e40af)', fontWeight: 700, background: 'var(--info-bg, #dbeafe)', border: '1px solid var(--info-border, #93c5fd)', padding: '4px 12px', borderRadius: '999px', boxShadow: '0 0 10px rgba(59,130,246,0.3)', cursor: 'pointer', fontSize: 'var(--fs-md)' }}
                                    >
                                      + Add New Train
                                    </button>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label className="form-label">Reference Document</label>
                      <input className="form-control" value={equipmentFormData.reference_document || ''} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, reference_document: e.target.value })} placeholder="Document name, URL, or file path" />
                    </div>
                    <FileUploader
                      label="Equipment Files"
                      files={equipmentFormData.attachedFiles}
                      onAdd={(files) => setEquipmentFormData((prev) => ({ ...prev, attachedFiles: [...parseFiles(prev.attachedFiles), ...files] }))}
                      onRemove={(idx) => setEquipmentFormData((prev) => ({ ...prev, attachedFiles: parseFiles(prev.attachedFiles).filter((_, i) => i !== idx) }))}
                      id="equipmentFiles"
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows="3" value={equipmentFormData.equipment_notes || ''} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, equipment_notes: e.target.value })} />
                  </div>
                </div>

                <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '20px' }}>
                    <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                      <span style={{ width: '4px', height: '20px', background: 'var(--primary-color, #2563eb)', borderRadius: '2px' }}></span>
                      Parts / Surface Areas
                    </h4>
                    <button type="button" className="btn btn-secondary" onClick={addEquipmentPartToForm}>
                      <FaPlus /> Add Part
                    </button>
                  </div>

                  {(equipmentFormData.parts || []).map((part, index) => {
                    const searchTerm = String(partsSearchTerms[index] || '').trim().toLowerCase();
                    const partSuggestions = equipmentParts
                      .filter((candidate) => {
                        const partCode = String(candidate.part_code || '').toLowerCase();
                        const partName = String(candidate.part_name || '').toLowerCase();
                        if (!searchTerm) return Boolean(partCode || partName);
                        return partCode.includes(searchTerm) || partName.includes(searchTerm);
                      })
                      .slice(0, 8);

                    return (
                      <div key={index} style={{ background: 'var(--card-bg)', padding: '16px', borderRadius: '10px', marginBottom: '16px', border: '1px solid var(--border-color)' }}>
                        {(equipmentFormData.parts || []).length > 1 && (
                          <button type="button" className="btn btn-danger-outline btn-sm" onClick={() => removeEquipmentPartFromForm(index)}>
                            <FaTrash /> Remove
                          </button>
                        )}

                        <div className="form-group" style={{ position: 'relative' }}>
                          <label className="form-label">Search Existing Part</label>
                          {part.part_code || part.part_name ? (
                            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '0.25rem', padding: '0.375rem 0.75rem', minHeight: '38px' }}>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--fs-base)' }}>
                                {[part.part_code, part.part_name].filter(Boolean).join(' - ')}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  handleEquipmentPartChange(index, 'part_code', '');
                                  handleEquipmentPartChange(index, 'part_name', '');
                                  setPartsSearchTerms({ ...partsSearchTerms, [index]: '' });
                                  setActivePartDropdownIndex(index);
                                }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--danger-color)', cursor: 'pointer', padding: '0 0 0 8px' }}>
                                x
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                className="form-control"
                                autoComplete="off"
                                name={`equipment_part_lookup_${index}`}
                                data-form-type="other"
                                data-lpignore="true"
                                spellCheck={false}
                                value={partsSearchTerms[index] || ''}
                                onFocus={() => setActivePartDropdownIndex(index)}
                                onBlur={() => setTimeout(() => setActivePartDropdownIndex((current) => (current === index ? null : current)), 200)}
                                onChange={(e) => {
                                  setPartsSearchTerms({ ...partsSearchTerms, [index]: e.target.value });
                                  setActivePartDropdownIndex(index);
                                }}
                                placeholder="Search part code or name..."
                              />
                              {activePartDropdownIndex === index && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.08)' }}>
                                  {partSuggestions.length === 0 ? (
                                    <div style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>No matching parts found.</div>
                                  ) : (
                                    partSuggestions.map((candidate, suggestionIndex) => (
                                      <button
                                        key={`${candidate.part_code || 'part'}-${candidate.part_name || ''}-${suggestionIndex}`}
                                        type="button"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          applyPartSuggestion(index, candidate);
                                          setActivePartDropdownIndex(null);
                                        }}
                                        style={{ width: '100%', textAlign: 'left', border: 'none', background: 'var(--card-bg)', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}>
                                        <strong>{candidate.part_code || '-'}</strong> - {candidate.part_name || '-'}
                                      </button>
                                    ))
                                  )}

                                  <button
                                    type="button"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setActivePartDropdownIndex(null);
                                      addEquipmentPartToForm();
                                    }}
                                    style={{ width: '100%', textAlign: 'center', border: 'none', background: 'var(--bg-color)', padding: '10px 12px', cursor: 'pointer', fontWeight: 600, color: 'var(--text-primary)', borderTop: '1px solid var(--border-color)' }}>
                                    + Add New Part
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px', marginBottom: '12px' }}>
                          <div className="form-group">
                            <label className="form-label">Part Code *</label>
                            <input className="form-control" value={part.part_code || ''} onChange={(e) => handleEquipmentPartChange(index, 'part_code', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Part Name *</label>
                            <input className="form-control" value={part.part_name || ''} onChange={(e) => handleEquipmentPartChange(index, 'part_name', e.target.value)} />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                          <div className="form-group">
                            <label className="form-label">Contact Type</label>
                            <select className="form-control" value={part.contact_type || 'Direct'} onChange={(e) => handleEquipmentPartChange(index, 'contact_type', e.target.value)}>
                              <option value="Direct">Direct</option>
                              <option value="Indirect">Indirect</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Cleaning Method</label>
                            <select className="form-control" value={part.cleaning_method || 'Manual'} onChange={(e) => handleEquipmentPartChange(index, 'cleaning_method', e.target.value)}>
                              <option value="Manual">Manual</option>
                              <option value="CIP">CIP</option>
                              <option value="COP">COP</option>
                              <option value="WIP">WIP</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Material</label>
                            <input className="form-control" list={`materialOptions_${index}`} value={part.material_of_construction || ''} onChange={(e) => handleEquipmentPartChange(index, 'material_of_construction', e.target.value)} />
                            <datalist id={`materialOptions_${index}`}>
                              {materialOptions.map((material) => <option key={material} value={material} />)}
                            </datalist>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 1.2fr', gap: '12px', marginBottom: '12px' }}>
                          <div className="form-group">
                            <label className="form-label">Surface Area</label>
                            <input type="number" step="any" min="0" className="form-control" value={part.surface_area || ''} onChange={(e) => handleEquipmentPartChange(index, 'surface_area', e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Unit</label>
                            <select className="form-control" value={part.surface_area_unit || 'cm2'} onChange={(e) => handleEquipmentPartChange(index, 'surface_area_unit', e.target.value)}>
                              <option value="cm2">cm²</option>
                              <option value="m2">m²</option>
                              <option value="ft2">ft²</option>
                            </select>
                          </div>
                          <FileUploader
                            label="Part Attachments"
                            files={part.part_attachments}
                            onAdd={(files) => handleEquipmentPartChange(index, 'part_attachments', [...parseFiles(part.part_attachments), ...files])}
                            onRemove={(fileIndex) => handleEquipmentPartChange(index, 'part_attachments', parseFiles(part.part_attachments).filter((_, i) => i !== fileIndex))}
                            id={`partFiles_${index}`}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Use Type</label>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500 }}>
                              <input type="radio" name={`useType_${index}`} checked={Boolean(part.is_dedicated)} onChange={() => { handleEquipmentPartChange(index, 'is_dedicated', true); handleEquipmentPartChange(index, 'is_shared', false); }} />
                              Dedicated
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500 }}>
                              <input type="radio" name={`useType_${index}`} checked={part.is_shared !== false && !part.is_dedicated} onChange={() => { handleEquipmentPartChange(index, 'is_dedicated', false); handleEquipmentPartChange(index, 'is_shared', true); }} />
                              Shared
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            <ModalFooter>
              <button type="button" className="btn btn-outline" onClick={() => { setShowEquipmentModal(false); resetEquipmentForm(); }}>Cancel</button>
              <button type="button" className="btn btn-secondary" onClick={openESignModalForDraftSave}>
                <FaSave /> Save as Draft
              </button>
              <button type="button" className="btn btn-primary" onClick={openESignModalForActiveSave}>Submit for Approval</button>
            </ModalFooter>
        </Modal>
      )}

      <ChangeReferenceModal
        open={!!changeRefGate}
        qmsLinked={qmsLinked}
        busy={changeRefBusy}
        recordLabel={changeRefGate?.equipment?.equipment_name}
        onCancel={() => setChangeRefGate(null)}
        onConfirm={confirmChangeReference}
      />

      <ESignModal
        isOpen={showESignModal}
        onCancel={() => { setShowESignModal(false); setPendingEquipmentDelete(null); setPendingEquipmentBulkAction(null); setPendingEquipmentApproval(null); setEquipmentDeletionImpact(null); setESignData({ password: '', comment: '', decision: 'approve' }); }}
        message={pendingEquipmentImport
          ? `Bulk import equipment from "${pendingEquipmentImport.name}"? Records are created as Draft and need approval.`
          : pendingEquipmentDelete
          ? [
              getDeleteConfirmMessage(pendingEquipmentDelete.stage, pendingEquipmentDelete.equipment?.equipment_name || pendingEquipmentDelete.equipment?.equipment_code || 'equipment'),
              equipmentDeletionImpact && (equipmentDeletionImpact.trains?.length || equipmentDeletionImpact.products?.length)
                ? `Impact: linked to ${equipmentDeletionImpact.trains?.length || 0} active train(s)${equipmentDeletionImpact.products?.length ? ` used by ${equipmentDeletionImpact.products.length} active product(s) (${equipmentDeletionImpact.products.slice(0, 5).map((p) => p.product_code).join(', ')}${equipmentDeletionImpact.products.length > 5 ? ', ...' : ''})` : ''}.`
                : null,
            ].filter(Boolean).join(' ')
          : pendingEquipmentBulkAction
            ? (pendingEquipmentBulkAction.actionType === 'bulk_delete'
              ? `Deactivate ${pendingEquipmentBulkAction.equipmentIds?.length || 0} selected equipment item(s)?`
              : pendingEquipmentBulkAction.approvalType === 'delete'
                ? `Bulk ${pendingEquipmentBulkAction.stage === 'product' ? 'Product' : 'QA'} deactivation approval for ${pendingEquipmentBulkAction.equipmentIds?.length || 0} equipment item(s)?`
                : `Bulk approve ${pendingEquipmentBulkAction.equipmentIds?.length || 0} selected equipment item(s)?`)
          : pendingEquipmentApproval
            ? `Approve equipment "${pendingEquipmentApproval.equipment_name || pendingEquipmentApproval.equipment_code || 'equipment'}"?`
            : isDraftESignMode
              ? `Save "${equipmentFormData.equipment_name || equipmentFormData.equipment_code || 'equipment'}" as draft?`
              : `Submit "${equipmentFormData.equipment_name || equipmentFormData.equipment_code || 'equipment'}" for approval?`}
        showDecision={!!(pendingEquipmentApproval || (pendingEquipmentBulkAction && pendingEquipmentBulkAction.approvalType !== 'delete' && pendingEquipmentBulkAction.actionType !== 'bulk_delete'))}
        decisionOptions={APPROVAL_DECISION_OPTIONS}
        decisionValue={eSignData.decision}
        onDecisionChange={(value) => setESignData({ ...eSignData, decision: value })}
        commentValue={eSignData.comment}
        onCommentChange={(value) => setESignData({ ...eSignData, comment: value })}
        commentPlaceholder={pendingEquipmentDelete
          ? `Reason for ${getDeleteActionLabel(pendingEquipmentDelete.stage)}`
          : pendingEquipmentBulkAction
            ? (pendingEquipmentBulkAction.actionType === 'bulk_delete'
              ? 'Reason for bulk deactivation'
              : pendingEquipmentBulkAction.approvalType === 'delete'
                ? `Reason for bulk ${pendingEquipmentBulkAction.stage === 'product' ? 'Product' : 'QA'} deactivation approval`
                : 'Reason for bulk approval')
            : pendingEquipmentApproval
              ? 'Reason for approval'
              : (isDraftESignMode ? 'Reason for draft save' : 'Reason for submission')}
        passwordValue={eSignData.password}
        onPasswordChange={(value) => setESignData({ ...eSignData, password: value })}
        onConfirm={() => handleConfirmESignAndSave()}
        confirmLabel={(pendingEquipmentApproval || (pendingEquipmentBulkAction && pendingEquipmentBulkAction.approvalType !== 'delete' && pendingEquipmentBulkAction.actionType !== 'bulk_delete'))
          ? 'Sign & Submit'
          : pendingEquipmentDelete
            ? (pendingEquipmentDelete.stage === 'draft' ? 'Confirm Deactivate' : 'Confirm & Submit')
            : pendingEquipmentBulkAction
              ? 'Confirm Bulk Deactivate'
              : (isDraftESignMode ? 'Confirm & Save Draft' : 'Confirm & Submit')}
      />
      {showEquipmentViewModal && viewingEquipment && (
        <Modal isOpen={showEquipmentViewModal} size="lg" title="Equipment Details" onClose={() => setShowEquipmentViewModal(false)}>
            <ModalSubheader>
              <span>Unit / Plant: {localStorage.getItem('activePlant') || 'N/A'}</span>
            </ModalSubheader>
            <div id="equipment-details-print">
              {viewLoading ? (
                <div className="loading-container"><div className="spinner"></div></div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Equipment Code</label>
                      <p>{viewingEquipment.equipment_code || '-'}</p>
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Status</label>
                      <p>
                        {(() => {
                          const statusInfo = getEquipmentStatusInfo(viewingEquipment);
                          return (
                            <span className={`badge ${statusInfo.badgeClass}`} title={statusInfo.tooltip}>
                              {statusInfo.text}
                            </span>
                          );
                        })()}
                      </p>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Equipment Name</label>
                      <p>{viewingEquipment.equipment_name || '-'}</p>
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Type</label>
                      <p><span className="badge badge-primary">{viewingEquipment.equipment_type || '-'}</span></p>
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Section</label>
                      <p>{viewingEquipment.production_line || viewingEquipment.production_line_name || localStorage.getItem('activeLine') || '-'}</p>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Available In Trains</label>
                      {viewEquipmentTrainLinks.length === 0 ? (
                        <>
                          <p className="text-muted">Not linked to any equipment train</p>
                          {viewTrainDataWarning && (
                            <div style={{ marginTop: '6px', padding: '8px 10px', border: '1px solid var(--warning-border, #f59e0b)', background: 'var(--warning-bg, #fffbeb)', color: 'var(--warning-text, #92400e)', borderRadius: '6px', fontSize: 'var(--fs-md)', fontWeight: 600 }}>
                              {viewTrainDataWarning}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                            {viewEquipmentTrainLinks.map((train) => (
                              <button
                                key={train.train_id}
                                type="button"
                                className="badge badge-info"
                                style={{ padding: '6px 10px', cursor: 'pointer', textDecoration: 'underline' }}
                                title="Open this equipment train"
                                onClick={() => navigate('/equipment-trains', { state: { openViewTrainId: train.train_id } })}
                              >
                                {train.train_code} - {train.train_name}
                              </button>
                            ))}
                          </div>
                          {viewTrainDataWarning && (
                            <div style={{ marginTop: '8px', padding: '8px 10px', border: '1px solid var(--warning-border, #f59e0b)', background: 'var(--warning-bg, #fffbeb)', color: 'var(--warning-text, #92400e)', borderRadius: '6px', fontSize: 'var(--fs-md)', fontWeight: 600 }}>
                              {viewTrainDataWarning}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <hr />
                  <h4>Equipment Parts</h4>
                  {equipmentParts.filter(p => p.equipment_id === viewingEquipment.equipment_id).length === 0 ? (
                    <p className="text-muted">No parts attached.</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                      {equipmentParts.filter(p => p.equipment_id === viewingEquipment.equipment_id).map(part => (
                        <div key={part.equipment_part_id} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px', background: 'var(--card-bg)' }}>
                          <div style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                            {part.part_code} - {part.part_name}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: 'var(--fs-base)' }}>
                            <div>
                              <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Contact Type</label>
                              <p><span className="badge badge-info">{part.contact_type ? normalizeContactType(part.contact_type) : '-'}</span></p>
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Material</label>
                              <p>{part.material_of_construction || '-'}</p>
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Cleaning Method</label>
                              <p>{part.cleaning_method || 'Manual'}</p>
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Surface Area</label>
                              <p>{part.surface_area || '-'} {formatSurfaceAreaUnitLabel(part.surface_area_unit)}</p>
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Usage Type</label>
                              <p>{(part.is_dedicated === true || Number(part.is_dedicated) === 1) ? 'Dedicated' : 'Shared'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <hr />
                  <h4>Reference & Notes</h4>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Reference Document</label>
                    <p>{viewingEquipment.reference_document || '-'}</p>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Notes</label>
                    <p>{viewingEquipment.equipment_notes || '-'}</p>
                  </div>
                </>
              )}
            </div>
            <ModalFooter>
              <button className="btn btn-outline" type="button" onClick={printEquipmentDetails}><FaPrint style={{ marginRight: 4 }} />Print</button>
              <button className="btn btn-primary" onClick={() => { setShowEquipmentViewModal(false); handleEquipmentEdit(viewingEquipment); }}>Edit</button>
              {normalizeDisplayStatus(viewingEquipment.status) === EQUIPMENT_STATUS.PENDING_APPROVAL && !viewingEquipment.deletion_requested && (
                <button className="btn btn-success" onClick={() => { setShowEquipmentViewModal(false); handleEquipmentStageApproval(viewingEquipment); }}>Approve / Reject</button>
              )}
            </ModalFooter>
        </Modal>
      )}

      {/* Add to Existing Train Modal */}
      {showAddToTrainModal && (
        <Modal isOpen={showAddToTrainModal} size="md" title="Add to Existing Train" onClose={() => { setShowAddToTrainModal(false); setTargetTrainId(''); setTargetTrainSearchTerm(''); setShowTargetTrainDropdown(false); }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-primary)', fontSize: 'var(--fs-base)', marginBottom: '10px', display: 'block' }}>Select Equipment Train</label>
                <div style={{ position: 'relative' }}>
                  {targetTrainId ? (
                    <div style={{ display: 'flex', alignItems: 'center', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.625rem 0.875rem', minHeight: '38px' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--fs-base)' }}>
                        {(() => {
                          const train = trains.find((t) => String(t.train_id) === String(targetTrainId));
                          return getTrainDisplayLabel(train);
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setTargetTrainId('');
                          setTargetTrainSearchTerm('');
                          setShowTargetTrainDropdown(true);
                        }}
                        style={{ border: 'none', background: 'transparent', color: 'var(--danger-color)', cursor: 'pointer', padding: '0 0 0 8px' }}
                      >
                        x
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        placeholder="Search train code or name..."
                        value={targetTrainSearchTerm}
                        onChange={(e) => setTargetTrainSearchTerm(e.target.value)}
                        onFocus={() => {
                          if (!trains.length) refreshTrainOptions();
                          setShowTargetTrainDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowTargetTrainDropdown(false), 200)}
                        style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', width: '100%' }}
                      />
                      {showTargetTrainDropdown && (
                        <div
                          style={{ position: 'relative', marginTop: '4px', zIndex: 1, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)', maxHeight: '220px', display: 'flex', flexDirection: 'column', marginTop: '4px', overflow: 'hidden' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                            {trains.length === 0 && (
                              <div style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No trains loaded</div>
                            )}
                            {trains
                              .filter((t) =>
                                String(t.train_code || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase()) ||
                                String(t.train_name || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase())
                              )
                              .map((t) => (
                                <div
                                  key={t.train_id}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setTargetTrainId(t.train_id);
                                    setTargetTrainSearchTerm('');
                                    setShowTargetTrainDropdown(false);
                                  }}
                                  style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: 'var(--fs-base)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.06))'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--card-bg)'}
                                >
                                  <strong>{getTrainCodeLabel(t)}</strong> - {t.train_name}
                                </div>
                              ))}
                            {trains.filter((t) =>
                              String(t.train_code || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase()) ||
                              String(t.train_name || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase())
                            ).length === 0 && (
                              <div style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>
                                No matches found.
                              </div>
                            )}
                          </div>
                          <div style={{ borderTop: '1px solid var(--border-color)', padding: '8px 10px', background: 'var(--card-bg)' }}>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setShowAddToTrainModal(false);
                                setTargetTrainId('');
                                setTargetTrainSearchTerm('');
                                setShowTargetTrainDropdown(false);
                                navigate('/equipment-trains', { state: { openAddTrainModal: true } });
                              }}
                            >
                              + Add New Train
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            <ModalFooter>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setShowAddToTrainModal(false); setTargetTrainId(''); setTargetTrainSearchTerm(''); setShowTargetTrainDropdown(false); }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddToExistingTrain} disabled={!targetTrainId}>Proceed</button>
            </ModalFooter>
        </Modal>
      )}
      <VersionHistoryModal
        isOpen={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={historyModal ? `Version History — ${historyModal.equipment_name}` : ''}
        fetcher={() => equipmentAPI.getRevisions(historyModal.equipment_id)}
      />
    </div>
  );
};

export default EquipmentMaster;



